import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconMap, IconChevronLeft, IconChevronRight, IconArrowLeft, IconRoute, IconLink, IconDownload, IconTrash } from '@tabler/icons-react';
import { functions, db, storage } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { usePostHog } from 'posthog-js/react';
import ActivityMap from '../components/ActivityMap';
import ActivityMap3D from '../components/ActivityMap3D';
import ActivityMap2DSearch from '../components/ActivityMap2DSearch';
import SegmentsList from '../components/SegmentsList';
import StyleSelectionSection from '../components/create/StyleSelectionSection';
import DimensionSelectionSection from '../components/create/DimensionSelectionSection';
import FormatSelectionSection from '../components/create/FormatSelectionSection';
import LegendSection from '../components/create/LegendSection';
import Footer from '../components/Footer';
import { useAdminView } from '../contexts/AdminViewContext';
import { buildLegendPreview } from '../utils/legendPreview';
import { getZwiftWorldForActivity, isZwiftMapStyleName } from '../utils/zwiftActivity';
import {
  readCachedStravaActivities,
  writeCachedStravaActivities,
} from '../utils/stravaActivitiesCache';
import './CreatePage.css';

const { build3DMapDebugSummaryHtml } = require('../utils/map3dDebugSummary');
const { build2DMapDebugSummaryHtml } = require('../utils/map2dDebugSummary');

// Strava OAuth config
const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write,profile:read_all";
const DEFAULT_3D_MAP_STYLE_URL = 'mapbox://styles/samuelcollin3333/cmf525thz00xg01rk0ewt0i6f';
const DEFAULT_3D_RENDER_MODE = 'browser_single';
const RENDERER_STATUS_POLL_INTERVAL_MS = 2500;
const RENDERER_STATUS_TIMEOUT_MS = 5 * 60 * 1000;
const RENDERER_PREWARM_POLL_INTERVAL_MS = 2500;
const CREATE_PAGE_REFRESH_ACTIVITIES_EVENT = 'mythicmaps:create:refresh-activities';
const DEFAULT_RENDER_SCORE_MULTIPLIERS = Object.freeze({
  coverageRewardMultiplier: 5,
  footprintRewardMultiplier: 1
});
// No lineWidth here: when omitted, the 3D renderer's own default applies
// (matches buildAutoUpload3DRendererOptions on the backend).
const DEFAULT_3D_RENDERER_OPTIONS = Object.freeze({
  style: DEFAULT_3D_MAP_STYLE_URL,
  ...DEFAULT_RENDER_SCORE_MULTIPLIERS,
  autoCameraSearch: true,
  cameraSearchPitches: [60],
  coarseBearingCount: 18,
  refineBearingOffsets: [],
  robustRetryLimit: 3,
  tightenZoomStep: 0.18,
  tightenMaxSteps: 8
});
const DEFAULT_WATOPIA_3D_RENDERER_LINE_WIDTH = 4;
const EMPTY_3D_RENDERER_RENDER_STATE = Object.freeze({
  status: 'idle',
  message: '',
  jobId: '',
  workerId: '',
  delayTime: null,
  executionTime: null,
  error: ''
});
const createEmpty3DRendererCacheEntry = (orientation) => ({
  cacheKey: '',
  orientation,
  status: 'idle',
  message: '',
  jobId: '',
  workerId: '',
  delayTime: null,
  executionTime: null,
  imagePath: '',
  debugHtmlPath: '',
  error: '',
  startedAtMs: null,
  updatedAtMs: null,
  staleAfterMs: null,
  isStale: false,
  isCached: false
});
const createEmpty3DRendererCacheState = () => ({
  landscape: createEmpty3DRendererCacheEntry('landscape'),
  portrait: createEmpty3DRendererCacheEntry('portrait')
});
const sanitizeDebugFileName = (value) => String(value || 'route')
  .trim()
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase() || 'route';
const normalize3DRenderMode = (value) => {
  if (value === 'renderer') return 'renderer';
  if (value === 'browser_search') return 'browser_search';
  return DEFAULT_3D_RENDER_MODE;
};

const activityIdKey = (activity) => (
  activity?.id === undefined || activity?.id === null ? '' : String(activity.id)
);

const countNewActivities = (freshActivities, cachedActivities) => {
  const cachedIds = new Set((cachedActivities || []).map(activityIdKey).filter(Boolean));
  return (freshActivities || []).filter((activity) => {
    const id = activityIdKey(activity);
    return id && !cachedIds.has(id);
  }).length;
};

const CreatePage = ({ user, onCreditsUpdated }) => {
  const navigate = useNavigate();
  const { activityId } = useParams(); // Get activity ID from URL if present
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { actualIsAdmin } = useAdminView();
  const posthog = usePostHog();

  // Challenge generation params (from /challenge/:id "Create Map" button)
  const challengeId = searchParams.get('challenge');
  const challengeStyle = searchParams.get('style');
  const styleDimension = searchParams.get('dim'); // '2d' | '3d' from the /maps style gallery
  const isFreeGeneration = searchParams.get('free') === 'true' && !!challengeId;
  // Set by the "Restyle in a Mythic style" CTA on the map page: the user
  // already has the free Zwift map, so don't auto-select the Zwift Map style.
  const restyleIntent = searchParams.get('restyle') === '1';
  // Apply the style/dimension from the URL only once, so it never overrides a
  // style the user later picks from the visible picker.
  const hasAppliedUrlStyleRef = useRef(false);
  
  // Step 1: Activity Selection
  const [activities, setActivities] = useState([]);
  const [allActivities, setAllActivities] = useState([]); // Store all activities including those without GPS
  const [currentPage, setCurrentPage] = useState(0); // For pagination (0-indexed)
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [activityRefreshNotice, setActivityRefreshNotice] = useState(null);
  const [stravaError, setStravaError] = useState(null); // For Strava permission errors
  const [monthlyUsage, setMonthlyUsage] = useState({ remaining: 0 });

  // State for direct activity link (when activityId is in URL)
  const [isLoadingTargetActivity, setIsLoadingTargetActivity] = useState(false);
  const [targetActivityError, setTargetActivityError] = useState(null);

  // Strava activity ID/URL manual input
  const [stravaIdInput, setStravaIdInput] = useState('');
  const [stravaIdError, setStravaIdError] = useState('');

  // State for segment efforts
  const [segmentEfforts, setSegmentEfforts] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [isLoadingSegments, setIsLoadingSegments] = useState(false);
  const [isSegmentsExpanded, setIsSegmentsExpanded] = useState(false);
  const [isLoadingSegmentPolyline, setIsLoadingSegmentPolyline] = useState(false);

  // Step 2: Style Selection
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [styleGroups, setStyleGroups] = useState([]);
  const [selectedStyleGroup, setSelectedStyleGroup] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [currentStylePage, setCurrentStylePage] = useState(0);
  const [activeTag, setActiveTag] = useState(null);
  const [tagCategories, setTagCategories] = useState({}); // fetched from Firestore
  const [activeTagCategory, setActiveTagCategory] = useState(null);
  const STYLES_PER_PAGE = 9;

  const [globalStyleOrder, setGlobalStyleOrder] = useState([]); // admin-set default order
  const [hiddenStyles, setHiddenStyles] = useState([]); // admin-hidden styles
  
  // Step 3: 2D/3D Selection (shown after style selected)
  const [mapDimension, setMapDimension] = useState('3d'); // '2d' or '3d'
  const [force2DMapGeneration, setForce2DMapGeneration] = useState(false);

  // Step 4: Map Format Toggle (Portrait/Landscape)
  const [mapFormat, setMapFormat] = useState('portrait'); // 'portrait' or 'landscape'
  const selectedZwiftWorld = useMemo(
    () => getZwiftWorldForActivity(selectedActivity),
    [selectedActivity]
  );
  const selectedActivityIsZwift = Boolean(selectedZwiftWorld);
  // Zwift Map renders for Zwift activities skip the AI step and are free.
  const isFreeZwiftRender = selectedActivityIsZwift && isZwiftMapStyleName(selectedStyleGroup?.name || '');
  // Worlds whose 3D map renders on the backend Zwift terrain renderer (keep
  // in sync with ZWIFT_RENDERER_3D_WORLD_SLUGS in functions/src/zwift_map_renderer.ts).
  const selectedActivityHas3DWorld = [
    'watopia', 'paris', 'richmond', 'london', 'new-york', 'innsbruck',
    'bologna', 'yorkshire', 'crit-city', 'makuri-islands', 'france', 'scotland',
  ].includes(selectedZwiftWorld?.slug);
  const selectedZwiftActivityForces2D = selectedActivityIsZwift && !selectedActivityHas3DWorld;
  const selectedStyleGroupHas3DOption = Boolean(
    selectedStyleGroup?.has3D || (selectedActivityHas3DWorld && selectedStyleGroup?.has2D)
  );

  // User setting from /settings: lock the map bearing to north instead of the
  // auto bearing search. Zwift rides always use auto search.
  const [userPrefersNorthUp, setUserPrefersNorthUp] = useState(false);
  const northUpEnabled = userPrefersNorthUp && !selectedActivityIsZwift;

  // Legend customisation
  const [customTitle, setCustomTitle] = useState('');
  const [distanceUnit, setDistanceUnit] = useState('metric'); // 'metric' or 'imperial'
  const [isLegendExpanded, setIsLegendExpanded] = useState(false);

  const isPortraitMap = mapFormat === 'portrait';
  
  // 3D Map state
  const [captured3DImage, setCaptured3DImage] = useState(null);
  const [isPreparing3DMap, setIsPreparing3DMap] = useState(false);
  const [isShowing3DDebugRenderer, setIsShowing3DDebugRenderer] = useState(false);
  const [isRendering3DOnly, setIsRendering3DOnly] = useState(false);
  const [map3DProgressText, setMap3DProgressText] = useState('');
  const [map3DProgressRatio, setMap3DProgressRatio] = useState(0);
  const [map3DDebugCandidates, setMap3DDebugCandidates] = useState([]);
  const [show3DDebugMaps, setShow3DDebugMaps] = useState(false);
  const [isRunning3DDebugPreview, setIsRunning3DDebugPreview] = useState(false);
  const [generationStatusText, setGenerationStatusText] = useState('');
  const [threeDRenderMode, setThreeDRenderMode] = useState(DEFAULT_3D_RENDER_MODE);
  const [renderer3DRenderState, setRenderer3DRenderState] = useState(EMPTY_3D_RENDERER_RENDER_STATE);
  const [renderer3DCacheState, setRenderer3DCacheState] = useState(createEmpty3DRendererCacheState);
  const [renderer3DDeletingCacheKey, setRenderer3DDeletingCacheKey] = useState('');
  const [renderer3DDebugHtml, setRenderer3DDebugHtml] = useState('');
  const [renderer3DDebugHtmlUrl, setRenderer3DDebugHtmlUrl] = useState('');
  const [browser3DDebugHtml, setBrowser3DDebugHtml] = useState('');
  const [browser3DDebugHtmlUrl, setBrowser3DDebugHtmlUrl] = useState('');
  const [browser3DCameraSearchSummary, setBrowser3DCameraSearchSummary] = useState(null);
  const [show2DBearingSearch, setShow2DBearingSearch] = useState(false);
  const [map2DDebugCandidates, setMap2DDebugCandidates] = useState([]);
  const [browser2DCameraSearchSummary, setBrowser2DCameraSearchSummary] = useState(null);
  const [browser2DDebugHtml, setBrowser2DDebugHtml] = useState('');
  const [browser2DDebugHtmlUrl, setBrowser2DDebugHtmlUrl] = useState('');
  const [map2DSearchRunId, setMap2DSearchRunId] = useState(0);
  const [mapGenerationSettings, setMapGenerationSettings] = useState(null);
  const [server2DSearchStatus, setServer2DSearchStatus] = useState('idle'); // 'idle' | 'running' | 'done' | 'error'
  const [server2DSearchResult, setServer2DSearchResult] = useState(null);
  const [server2DSearchError, setServer2DSearchError] = useState('');
  const [threeDLineWidth, setThreeDLineWidth] = useState(null);
  const [threeDLineWidthConfigured, setThreeDLineWidthConfigured] = useState(false);
  const [threeDCoarseSearchCount, setThreeDCoarseSearchCount] = useState(DEFAULT_3D_RENDERER_OPTIONS.coarseBearingCount);
  const [threeDTightenCandidateCount, setThreeDTightenCandidateCount] = useState(DEFAULT_3D_RENDERER_OPTIONS.robustRetryLimit);
  const [threeDCoverageRewardMultiplier, setThreeDCoverageRewardMultiplier] = useState(DEFAULT_3D_RENDERER_OPTIONS.coverageRewardMultiplier);
  const [threeDFootprintRewardMultiplier, setThreeDFootprintRewardMultiplier] = useState(DEFAULT_3D_RENDERER_OPTIONS.footprintRewardMultiplier);
  const [is3DSearchControlsExpanded, setIs3DSearchControlsExpanded] = useState(false);
  const [isCreatePageAdminViewEnabled, setIsCreatePageAdminViewEnabled] = useState(false);
  const captureMapCallbackRef = useRef(null);
  const is3DMapReadyRef = useRef(false);
  const pending3DPreparationRef = useRef(null);
  const rendererPrewarmPollTimeoutRef = useRef(null);
  const rendererPrewarmRequestVersionRef = useRef(0);
  const last3DRendererPrewarmRouteKeyRef = useRef('');
  const hasLoadedInitialDataRef = useRef(null);
  const loadActivitiesRef = useRef(null);
  const preloadedExampleImageUrlsRef = useRef(new Set());
  const map3DDebugCandidatesRef = useRef([]);
  const allMap3DDebugCandidatesRef = useRef([]);
  const browser3DCameraSearchSummaryRef = useRef(null);
  const debugHtmlDownloadBaseName = useMemo(() => sanitizeDebugFileName(
    selectedSegment?.segment?.name || selectedActivity?.name || 'route'
  ), [selectedActivity?.name, selectedSegment?.segment?.name]);
  const debugHtmlDownloadNames = useMemo(() => ({
    browser: `${debugHtmlDownloadBaseName}-browser-3d-debug.html`,
    renderer: `${debugHtmlDownloadBaseName}-renderer-3d-debug.html`,
    browser2d: `${debugHtmlDownloadBaseName}-browser-2d-debug.html`
  }), [debugHtmlDownloadBaseName]);
  const isAdmin = actualIsAdmin && isCreatePageAdminViewEnabled;
  const threeDRenderSource = threeDRenderMode === 'renderer' ? 'renderer' : 'browser';
  const effectiveThreeDRenderSource = selectedActivityHas3DWorld ? 'renderer' : threeDRenderSource;
  const isBrowser3DSearchEnabled = threeDRenderMode === 'browser_search';

  useEffect(() => {
    setIsCreatePageAdminViewEnabled(false);
  }, [actualIsAdmin, user?.uid]);

  useEffect(() => {
    if (!actualIsAdmin) {
      return undefined;
    }

    const handleCreatePageAdminShortcut = (event) => {
      if (!((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'A')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      setIsCreatePageAdminViewEnabled((previousValue) => !previousValue);
    };

    window.addEventListener('keydown', handleCreatePageAdminShortcut, true);
    return () => {
      window.removeEventListener('keydown', handleCreatePageAdminShortcut, true);
    };
  }, [actualIsAdmin]);

  const preloadExampleImage = useCallback((url) => {
    if (!url || preloadedExampleImageUrlsRef.current.has(url)) {
      return;
    }

    preloadedExampleImageUrlsRef.current.add(url);
    const img = new Image();
    img.src = url;
  }, []);

  const preloadStyleGroupExamples = useCallback((styleGroup) => {
    if (!styleGroup?.templates?.length) {
      return;
    }

    styleGroup.templates.forEach((template) => {
      preloadExampleImage(template.exampleImageUrl);
    });
  }, [preloadExampleImage]);

  const resolvePending3DPreparation = useCallback(() => {
    const pending = pending3DPreparationRef.current;
    if (!pending) return;

    clearTimeout(pending.timeoutId);
    pending3DPreparationRef.current = null;
    pending.resolve();
  }, []);

  const rejectPending3DPreparation = useCallback((error) => {
    const pending = pending3DPreparationRef.current;
    if (!pending) return;

    clearTimeout(pending.timeoutId);
    pending3DPreparationRef.current = null;
    pending.reject(error instanceof Error ? error : new Error('Failed to prepare 3D map.'));
  }, []);

  const maybeResolve3DPreparation = useCallback(() => {
    if (captureMapCallbackRef.current && is3DMapReadyRef.current) {
      resolvePending3DPreparation();
    }
  }, [resolvePending3DPreparation]);

  const reset3DPreparationState = useCallback((options = {}) => {
    const {
      clearProgress = true,
      clearDebugCandidates = true,
      rejectPending = false,
      clearLiveDebugRenderer = true,
      rejectionMessage = '3D map preparation cancelled.'
    } = options;

    captureMapCallbackRef.current = null;
    is3DMapReadyRef.current = false;
    setIsPreparing3DMap(false);
    if (clearLiveDebugRenderer) {
      setIsShowing3DDebugRenderer(false);
    }

    if (clearProgress) {
      setMap3DProgressText('');
      setMap3DProgressRatio(0);
    }
    if (clearDebugCandidates) {
      setMap3DDebugCandidates([]);
      map3DDebugCandidatesRef.current = [];
      allMap3DDebugCandidatesRef.current = [];
    }
    if (rejectPending) {
      rejectPending3DPreparation(new Error(rejectionMessage));
    }
  }, [rejectPending3DPreparation]);

  const resetRenderer3DRenderState = useCallback(() => {
    setRenderer3DRenderState(EMPTY_3D_RENDERER_RENDER_STATE);
    setRenderer3DDebugHtml('');
  }, []);

  const stop3DRendererPrewarmPolling = useCallback(() => {
    if (rendererPrewarmPollTimeoutRef.current) {
      clearTimeout(rendererPrewarmPollTimeoutRef.current);
      rendererPrewarmPollTimeoutRef.current = null;
    }
  }, []);

  const resetRenderer3DCacheState = useCallback(() => {
    stop3DRendererPrewarmPolling();
    setRenderer3DCacheState(createEmpty3DRendererCacheState());
  }, [stop3DRendererPrewarmPolling]);

  const resetBrowser3DDebugState = useCallback(() => {
    browser3DCameraSearchSummaryRef.current = null;
    setBrowser3DDebugHtml('');
    setBrowser3DCameraSearchSummary(null);
  }, []);

  const clearCaptured3DImage = useCallback(() => {
    setCaptured3DImage((previousImage) => {
      if (previousImage?.objectUrl) {
        URL.revokeObjectURL(previousImage.objectUrl);
      }
      return null;
    });
  }, []);

  useEffect(() => {
    if (!force2DMapGeneration || mapDimension === '2d') {
      return;
    }

    reset3DPreparationState({ rejectPending: true });
    resetRenderer3DRenderState();
    resetBrowser3DDebugState();
    clearCaptured3DImage();
    setIsRunning3DDebugPreview(false);
    setGenerationStatusText('');
    setMapDimension('2d');
  }, [
    clearCaptured3DImage,
    force2DMapGeneration,
    mapDimension,
    reset3DPreparationState,
    resetBrowser3DDebugState,
    resetRenderer3DRenderState
  ]);

  useEffect(() => {
    if (!renderer3DDebugHtml) {
      setRenderer3DDebugHtmlUrl('');
      return undefined;
    }

    const debugHtmlBlob = new Blob([renderer3DDebugHtml], { type: 'text/html' });
    const nextDebugHtmlUrl = URL.createObjectURL(debugHtmlBlob);
    setRenderer3DDebugHtmlUrl(nextDebugHtmlUrl);

    return () => {
      URL.revokeObjectURL(nextDebugHtmlUrl);
    };
  }, [renderer3DDebugHtml]);

  useEffect(() => {
    if (!browser3DDebugHtml) {
      setBrowser3DDebugHtmlUrl('');
      return undefined;
    }

    const debugHtmlBlob = new Blob([browser3DDebugHtml], { type: 'text/html' });
    const nextDebugHtmlUrl = URL.createObjectURL(debugHtmlBlob);
    setBrowser3DDebugHtmlUrl(nextDebugHtmlUrl);

    return () => {
      URL.revokeObjectURL(nextDebugHtmlUrl);
    };
  }, [browser3DDebugHtml]);

  useEffect(() => {
    if (!browser2DDebugHtml) {
      setBrowser2DDebugHtmlUrl('');
      return undefined;
    }

    const debugHtmlBlob = new Blob([browser2DDebugHtml], { type: 'text/html' });
    const nextDebugHtmlUrl = URL.createObjectURL(debugHtmlBlob);
    setBrowser2DDebugHtmlUrl(nextDebugHtmlUrl);

    return () => {
      URL.revokeObjectURL(nextDebugHtmlUrl);
    };
  }, [browser2DDebugHtml]);

  useEffect(() => {
    if (!show2DBearingSearch || mapGenerationSettings !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'mapGeneration'));
        if (cancelled) return;
        setMapGenerationSettings(snap.exists() ? (snap.data() || {}) : {});
      } catch (error) {
        console.error('CreatePage: Failed to load mapGeneration settings:', error);
        if (!cancelled) setMapGenerationSettings({});
      }
    })();
    return () => { cancelled = true; };
  }, [show2DBearingSearch, mapGenerationSettings]);

  // Padding is forced to 100px for the bearing search (overrides settings/mapGeneration)
  // while we iterate on the overlay-based scoring. Revert to settings-based padding once
  // the logo/stats penalty is the right shape.
  const map2DSearchPadding = useMemo(() => ({ top: 100, bottom: 100, left: 100, right: 100 }), []);

  const map2DSearchOverlays = useMemo(() => ({
    logoWidth: 280,
    logoHeight: 280,
    statsWidth: 420,
    statsHeight: 280,
  }), []);

  const map2DSearchRouteStyle = useMemo(() => {
    const settings = mapGenerationSettings || {};
    const parsedWidth = Number(settings.routeLineWidth);
    const parsedOpacity = Number(settings.routeLineOpacity);
    return {
      lineWidth: Number.isFinite(parsedWidth) && parsedWidth > 0 ? parsedWidth : 5,
      lineColor: typeof settings.routeLineColor === 'string' && settings.routeLineColor
        ? (settings.routeLineColor.startsWith('#') ? settings.routeLineColor : `#${settings.routeLineColor}`)
        : '#FC5200',
      lineOpacity: Number.isFinite(parsedOpacity) ? Math.max(0, Math.min(1, parsedOpacity)) : 0.75,
    };
  }, [mapGenerationSettings]);

  // Fire the server-side 2D bearing search in parallel with the browser search
  // each time the user toggles the search on (or bumps the run id).
  useEffect(() => {
    if (!show2DBearingSearch || !selectedActivity) return;
    if (mapGenerationSettings === null) return; // wait until settings are loaded so padding matches

    const polylineString = selectedSegment?.segment?.map?.polyline
      || selectedSegment?.segment?.map?.summary_polyline
      || selectedActivity?.map?.summary_polyline
      || '';
    if (!polylineString) {
      setServer2DSearchStatus('error');
      setServer2DSearchError('No polyline found on activity/segment');
      setServer2DSearchResult(null);
      return;
    }

    let cancelled = false;
    setServer2DSearchStatus('running');
    setServer2DSearchError('');
    setServer2DSearchResult(null);

    const search2DBearingFunc = httpsCallable(functions, 'search2DBearing');
    const params = {
      polyline: polylineString,
      orientation: isPortraitMap ? 'portrait' : 'landscape',
      coarseBearingCount: 12,
      captureWidth: isPortraitMap ? 854 : 1280,
      captureHeight: isPortraitMap ? 1280 : 853,
      paddingTop: map2DSearchPadding.top,
      paddingBottom: map2DSearchPadding.bottom,
      paddingLeft: map2DSearchPadding.left,
      paddingRight: map2DSearchPadding.right,
      lineWidth: map2DSearchRouteStyle.lineWidth,
      lineColorHex: map2DSearchRouteStyle.lineColor,
      lineOpacity: map2DSearchRouteStyle.lineOpacity,
      logoOverlayWidth: map2DSearchOverlays.logoWidth,
      logoOverlayHeight: map2DSearchOverlays.logoHeight,
      statsOverlayWidth: map2DSearchOverlays.statsWidth,
      statsOverlayHeight: map2DSearchOverlays.statsHeight,
      includeDebugImages: true,
      debugImageMaxDim: 480,
    };

    (async () => {
      try {
        const response = await search2DBearingFunc(params);
        if (cancelled) return;
        setServer2DSearchResult(response?.data || null);
        setServer2DSearchStatus('done');
      } catch (error) {
        if (cancelled) return;
        console.error('CreatePage: server 2D bearing search failed:', error);
        setServer2DSearchError(error?.message || 'Server search failed');
        setServer2DSearchStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [
    show2DBearingSearch,
    map2DSearchRunId,
    selectedActivity,
    selectedSegment,
    mapGenerationSettings,
    isPortraitMap,
    map2DSearchPadding,
    map2DSearchRouteStyle,
    map2DSearchOverlays,
  ]);

  useEffect(() => {
    return () => {
      if (captured3DImage?.objectUrl) {
        URL.revokeObjectURL(captured3DImage.objectUrl);
      }
    };
  }, [captured3DImage]);

  useEffect(() => () => {
    stop3DRendererPrewarmPolling();
  }, [stop3DRendererPrewarmPolling]);

  useEffect(() => {
    map3DDebugCandidatesRef.current = map3DDebugCandidates;
  }, [map3DDebugCandidates]);

  useEffect(() => {
    browser3DCameraSearchSummaryRef.current = browser3DCameraSearchSummary;
  }, [browser3DCameraSearchSummary]);

  const blobToDataUrl = useCallback((blob) => new Promise((resolve, reject) => {
    if (!blob) {
      resolve('');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob as data URL.'));
    reader.readAsDataURL(blob);
  }), []);

  const waitFor3DPreparation = useCallback((timeoutMs = 60000) => {
    if (captureMapCallbackRef.current && is3DMapReadyRef.current) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        pending3DPreparationRef.current = null;
        reject(new Error('3D map preparation timed out. Please try again.'));
      }, timeoutMs);

      pending3DPreparationRef.current = {
        resolve,
        reject,
        timeoutId
      };
    });
  }, []);

  const getSelected3DPolylineString = useCallback(() => (
    selectedSegment?.segment?.map?.polyline ||
    selectedSegment?.segment?.map?.summary_polyline ||
    selectedActivity?.map?.summary_polyline ||
    ''
  ), [selectedActivity, selectedSegment]);

  const getSelected3DRendererRouteName = useCallback(() => (
    selectedSegment?.segment?.name || selectedActivity?.name || 'Mythic Maps Route'
  ), [selectedActivity?.name, selectedSegment?.segment?.name]);

  // Mirrors buildAutoUpload3DRendererOptions on the backend: only an
  // admin-configured routeLineWidth3d or the Watopia default forces a width;
  // when undefined, lineWidth is omitted so the renderer default applies.
  const effective3DLineWidth = threeDLineWidthConfigured
    ? threeDLineWidth
    : (selectedActivityHas3DWorld ? DEFAULT_WATOPIA_3D_RENDERER_LINE_WIDTH : undefined);

  const buildBase3DRendererOptions = useCallback(() => {
    const options = {
      ...DEFAULT_3D_RENDERER_OPTIONS,
      coarseBearingCount: threeDCoarseSearchCount,
      robustRetryLimit: threeDTightenCandidateCount,
      coverageRewardMultiplier: threeDCoverageRewardMultiplier,
      footprintRewardMultiplier: threeDFootprintRewardMultiplier
    };
    if (effective3DLineWidth !== undefined && effective3DLineWidth !== null) {
      options.lineWidth = effective3DLineWidth;
    }
    if (northUpEnabled) {
      // Locks the renderer camera search to bearing 0 (north-up).
      options.singleCandidateBearing = 0;
    }
    return options;
  }, [
    effective3DLineWidth,
    northUpEnabled,
    threeDCoarseSearchCount,
    threeDTightenCandidateCount,
    threeDCoverageRewardMultiplier,
    threeDFootprintRewardMultiplier
  ]);

  const build3DRendererOptionsForOrientation = useCallback((orientation) => ({
    ...buildBase3DRendererOptions(),
    width: orientation === 'portrait' ? 1024 : 1536,
    // Portrait keeps the same single 60° search pitch as landscape: adding
    // flatter candidates ([60, 45, 35]) let the footprint reward pick 35° for
    // nearly every portrait route, which rendered near-top-down maps.
    height: orientation === 'portrait' ? 1536 : 1024
  }), [buildBase3DRendererOptions]);

  const get3DRendererCacheStatusMessage = useCallback((entry) => {
    if (!entry || !entry.status || entry.status === 'idle') {
      return 'Waiting to start 3D renderer pre-render.';
    }

    if (entry.isCached || entry.status === 'COMPLETED') {
      return 'Cached 3D render is ready.';
    }

    if (entry.status === 'STARTING') {
      return 'Starting 3D renderer pre-render...';
    }

    if (entry.status === 'IN_QUEUE') {
      return '3D renderer pre-render is queued.';
    }

    if (entry.status === 'IN_PROGRESS') {
      return '3D renderer pre-render is running.';
    }

    if (entry.status === 'STALE') {
      return '3D renderer pre-render became stale and will be restarted when needed.';
    }

    if (['FAILED', 'CANCELLED', 'TIMED_OUT', 'ABORTED'].includes(entry.status)) {
      return entry.error || '3D renderer pre-render failed.';
    }

    return 'Waiting for 3D renderer pre-render...';
  }, []);

  const normalize3DRendererCacheEntry = useCallback((entry, orientation) => {
    const nextEntry = entry && typeof entry === 'object' ? entry : {};
    const nextStatus = nextEntry.status || 'idle';
    return {
      ...createEmpty3DRendererCacheEntry(orientation),
      ...nextEntry,
      orientation,
      status: nextStatus,
      cacheKey: nextEntry.cacheKey || '',
      jobId: nextEntry.jobId || '',
      workerId: nextEntry.workerId || '',
      imagePath: nextEntry.imagePath || '',
      debugHtmlPath: nextEntry.debugHtmlPath || '',
      error: nextEntry.error || '',
      delayTime: Number.isFinite(nextEntry.delayTime) ? nextEntry.delayTime : null,
      executionTime: Number.isFinite(nextEntry.executionTime) ? nextEntry.executionTime : null,
      startedAtMs: Number.isFinite(nextEntry.startedAtMs) ? nextEntry.startedAtMs : null,
      updatedAtMs: Number.isFinite(nextEntry.updatedAtMs) ? nextEntry.updatedAtMs : null,
      staleAfterMs: Number.isFinite(nextEntry.staleAfterMs) ? nextEntry.staleAfterMs : null,
      isStale: Boolean(nextEntry.isStale || nextStatus === 'STALE'),
      isCached: Boolean(nextEntry.isCached || nextStatus === 'COMPLETED'),
      message: get3DRendererCacheStatusMessage({
        ...nextEntry,
        status: nextStatus,
        isCached: Boolean(nextEntry.isCached || nextStatus === 'COMPLETED')
      })
    };
  }, [get3DRendererCacheStatusMessage]);

  const get3DRendererStatusMessage = useCallback((status, statusData = {}) => {
    switch (status) {
      case 'starting':
        return 'Submitting 3D renderer job...';
      case 'IN_QUEUE':
        return '3D renderer job queued...';
      case 'IN_PROGRESS':
        return '3D renderer job running...';
      case 'COMPLETED':
        return '3D renderer job complete.';
      case 'FAILED':
      case 'CANCELLED':
      case 'TIMED_OUT':
      case 'ABORTED':
        return statusData?.error || '3D renderer job failed.';
      default:
        return 'Waiting for 3D renderer job...';
    }
  }, []);

  const updateRenderer3DRenderState = useCallback((statusData) => {
    const nextStatus = statusData?.status || 'UNKNOWN';
    const nextMessage = get3DRendererStatusMessage(nextStatus, statusData);

    setRenderer3DRenderState({
      status: nextStatus,
      message: nextMessage,
      jobId: statusData?.jobId || '',
      workerId: statusData?.workerId || '',
      delayTime: Number.isFinite(statusData?.delayTime) ? statusData.delayTime : null,
      executionTime: Number.isFinite(statusData?.executionTime) ? statusData.executionTime : null,
      error: statusData?.error || ''
    });

    if (statusData?.debugSummaryHtml) {
      setRenderer3DDebugHtml(statusData.debugSummaryHtml);
    }

    setGenerationStatusText(nextMessage);
  }, [get3DRendererStatusMessage]);

  const capture3DMapVia3DRenderer = useCallback(async () => {
    const polylineString = getSelected3DPolylineString();
    if (!polylineString) {
      throw new Error('No route polyline is available for backend 3D rendering.');
    }

    const start3DRendererRender = httpsCallable(functions, 'start3DRendererRender');
    const get3DRendererRenderStatus = httpsCallable(functions, 'get3DRendererRenderStatus');
    const renderOptions = build3DRendererOptionsForOrientation(mapFormat);

    console.log('CreatePage: Starting 3D renderer job', {
      routeName: getSelected3DRendererRouteName(),
      polylineLength: polylineString.length,
      renderOptions
    });

    resetRenderer3DRenderState();
    updateRenderer3DRenderState({ status: 'starting' });

    let startResult;
    try {
      startResult = await start3DRendererRender({
        polyline: polylineString,
        routeName: getSelected3DRendererRouteName(),
        options: renderOptions
      });
    } catch (error) {
      console.error('CreatePage: start3DRendererRender failed', {
        error,
        code: error?.code,
        message: error?.message,
        details: error?.details
      });
      throw error;
    }

    console.log('CreatePage: 3D renderer enqueue response', startResult?.data || null);

    const jobId = startResult?.data?.jobId;
    if (!jobId) {
      throw new Error('The 3D renderer did not return a render job id.');
    }

    updateRenderer3DRenderState({
      status: startResult?.data?.status || 'IN_QUEUE',
      jobId
    });

    const startedAt = Date.now();
    while ((Date.now() - startedAt) < RENDERER_STATUS_TIMEOUT_MS) {
      await new Promise((resolve) => setTimeout(resolve, RENDERER_STATUS_POLL_INTERVAL_MS));
      let statusResult;
      try {
        statusResult = await get3DRendererRenderStatus({ jobId });
      } catch (error) {
        console.error('CreatePage: get3DRendererRenderStatus failed', {
          jobId,
          error,
          code: error?.code,
          message: error?.message,
          details: error?.details
        });
        throw error;
      }
      const statusData = statusResult?.data || {};
      console.log('CreatePage: 3D renderer status update', {
        jobId,
        status: statusData.status,
        workerId: statusData.workerId || null,
        delayTime: statusData.delayTime || null,
        executionTime: statusData.executionTime || null,
        hasImageData: Boolean(statusData.imageData),
        hasDebugSummaryHtml: Boolean(statusData.debugSummaryHtml),
        error: statusData.error || null
      });
      updateRenderer3DRenderState(statusData);

      if (statusData.status === 'COMPLETED') {
        if (!statusData.imageData) {
          throw new Error('The 3D renderer completed without returning an image.');
        }

        const imageResponse = await fetch(statusData.imageData);
        const imageBlob = await imageResponse.blob();
        if (!imageBlob.size) {
          throw new Error('The 3D renderer returned an empty image payload.');
        }

        return {
          blob: imageBlob,
          mimeType: imageBlob.type || 'image/png'
        };
      }

      if (['FAILED', 'CANCELLED', 'TIMED_OUT', 'ABORTED'].includes(statusData.status)) {
        throw new Error(statusData.error || '3D renderer job failed.');
      }
    }

    throw new Error('3D renderer job timed out.');
  }, [
    build3DRendererOptionsForOrientation,
    getSelected3DPolylineString,
    getSelected3DRendererRouteName,
    resetRenderer3DRenderState,
    mapFormat,
    updateRenderer3DRenderState
  ]);

  const buildSelected3DRendererRenderOptions = useCallback(() => (
    build3DRendererOptionsForOrientation(mapFormat)
  ), [build3DRendererOptionsForOrientation, mapFormat]);

  const getSelected3DRendererCacheEntry = useCallback((orientation = mapFormat) => {
    const nextOrientation = orientation === 'portrait' ? 'portrait' : 'landscape';
    const entry = renderer3DCacheState[nextOrientation];
    if (!entry?.cacheKey) {
      return null;
    }
    return entry;
  }, [mapFormat, renderer3DCacheState]);

  const handleRemove3DRendererCache = useCallback(async ({
    cacheKey = '',
    orientation = mapFormat,
    rendererType = selectedActivityHas3DWorld ? (selectedZwiftWorld?.slug || 'watopia') : 'mapbox'
  } = {}) => {
    if (!isAdmin || renderer3DDeletingCacheKey) {
      return;
    }

    const nextOrientation = orientation === 'portrait' ? 'portrait' : 'landscape';
    const normalizedCacheKey = typeof cacheKey === 'string' ? cacheKey.trim() : '';
    const polylineString = getSelected3DPolylineString();
    if (!normalizedCacheKey && !polylineString) {
      setError('No route polyline is available for cache removal.');
      return;
    }

    const confirmLabel = normalizedCacheKey || `${rendererType} ${nextOrientation} cache for this route`;
    const shouldRemove = window.confirm(`Remove ${confirmLabel}? The next generation will render it again.`);
    if (!shouldRemove) {
      return;
    }

    const deletingKey = normalizedCacheKey || `${rendererType}:${selectedActivity?.id || 'route'}:${nextOrientation}`;
    const delete3DRendererCacheFunc = httpsCallable(functions, 'delete3DRendererCache');

    setRenderer3DDeletingCacheKey(deletingKey);
    setError('');

    try {
      const payload = normalizedCacheKey ? {
        cacheKey: normalizedCacheKey
      } : {
        polyline: polylineString,
        activityId: selectedActivity?.id?.toString?.() || `${selectedActivity?.id || ''}`,
        ...(selectedSegment?.segment?.id ? { segmentId: `${selectedSegment.segment.id}` } : {}),
        orientation: nextOrientation,
        rendererType
      };
      const result = await delete3DRendererCacheFunc(payload);
      const deletedCacheKey = result?.data?.cacheKey || normalizedCacheKey;
      const wasDeleted = Boolean(result?.data?.deleted);

      setRenderer3DCacheState((previousState) => {
        const nextState = {
          landscape: previousState.landscape,
          portrait: previousState.portrait
        };

        ['landscape', 'portrait'].forEach((entryOrientation) => {
          const entry = previousState[entryOrientation];
          const shouldClearEntry = entry?.cacheKey && (
            entry.cacheKey === deletedCacheKey ||
            (!normalizedCacheKey && entryOrientation === nextOrientation)
          );
          if (shouldClearEntry) {
            nextState[entryOrientation] = createEmpty3DRendererCacheEntry(entryOrientation);
          }
        });

        return nextState;
      });

      setRenderer3DRenderState((previousState) => ({
        ...previousState,
        status: wasDeleted ? 'deleted' : 'idle',
        message: wasDeleted ? 'Removed cached 3D render. The next generation will render a fresh map.' : 'No matching cached 3D render was found.',
        error: ''
      }));
    } catch (cacheDeleteError) {
      console.error('CreatePage: Failed to delete 3D renderer cache', cacheDeleteError);
      const message = cacheDeleteError?.message || 'Failed to remove cached 3D render.';
      setRenderer3DRenderState((previousState) => ({
        ...previousState,
        status: 'FAILED',
        message,
        error: message
      }));
      setError(message);
    } finally {
      setRenderer3DDeletingCacheKey('');
    }
  }, [
    getSelected3DPolylineString,
    isAdmin,
    mapFormat,
    renderer3DDeletingCacheKey,
    selectedActivity?.id,
    selectedActivityHas3DWorld,
    selectedSegment?.segment?.id
  ]);

  useEffect(() => {
    if (selectedActivityIsZwift || threeDRenderSource !== 'renderer') {
      resetRenderer3DCacheState();
      last3DRendererPrewarmRouteKeyRef.current = '';
      return undefined;
    }

    const selectedRoutePolylineString = getSelected3DPolylineString();
    if (!selectedActivity || !selectedRoutePolylineString) {
      resetRenderer3DCacheState();
      last3DRendererPrewarmRouteKeyRef.current = '';
      return undefined;
    }

    // The bearing mode is part of the key so a prewarm started before the
    // user's north-up preference resolves gets redone with the right options.
    const routeKey = (selectedSegment?.segment?.id ?
      `${selectedActivity.id}:segment:${selectedSegment.segment.id}` :
      `${selectedActivity.id}:full`) + (northUpEnabled ? ':north' : ':auto');
    const routeChanged = last3DRendererPrewarmRouteKeyRef.current !== routeKey;
    const shouldPrewarm = routeChanged;

    if (!shouldPrewarm) {
      return undefined;
    }

    const ensure3DRendererCacheFunc = httpsCallable(functions, 'ensure3DRendererCache');
    const refresh3DRendererCacheStatusFunc = httpsCallable(functions, 'refresh3DRendererCacheStatus');
    const requestVersion = ++rendererPrewarmRequestVersionRef.current;
    let isCancelled = false;
    let startTimeoutId = null;

    const applyRenderEntries = (renders = []) => {
      if (isCancelled || rendererPrewarmRequestVersionRef.current !== requestVersion) {
        return [];
      }

      const renderByOrientation = new Map(
        renders
          .filter((render) => render?.orientation)
          .map((render) => [render.orientation === 'portrait' ? 'portrait' : 'landscape', render])
      );

        setRenderer3DCacheState((previousState) => ({
          landscape: normalize3DRendererCacheEntry(
            renderByOrientation.get('landscape') || previousState.landscape,
            'landscape'
          ),
          portrait: normalize3DRendererCacheEntry(
            renderByOrientation.get('portrait') || previousState.portrait,
            'portrait'
          )
        }));

      return renders
        .filter((render) => render?.cacheKey && !['COMPLETED', 'FAILED', 'CANCELLED', 'TIMED_OUT', 'ABORTED', 'STALE'].includes(render.status))
        .map((render) => render.cacheKey);
    };

    const scheduleRefresh = (cacheKeys) => {
      stop3DRendererPrewarmPolling();
      if (isCancelled || !Array.isArray(cacheKeys) || !cacheKeys.length) {
        return;
      }

      rendererPrewarmPollTimeoutRef.current = setTimeout(async () => {
        try {
          const refreshResult = await refresh3DRendererCacheStatusFunc({ cacheKeys });
          const nextActiveKeys = applyRenderEntries(refreshResult?.data?.renders || []);
          scheduleRefresh(nextActiveKeys);
        } catch (error) {
          if (!isCancelled) {
            console.error('CreatePage: Failed to refresh 3D renderer pre-render cache status', error);
          }
        }
      }, RENDERER_PREWARM_POLL_INTERVAL_MS);
    };

    const ensurePrewarm = async () => {
      try {
        const ensureResult = await ensure3DRendererCacheFunc({
          polyline: selectedRoutePolylineString,
          routeName: getSelected3DRendererRouteName(),
          activityId: selectedActivity.id?.toString?.() || `${selectedActivity.id}`,
          ...(selectedSegment?.segment?.id ? { segmentId: `${selectedSegment.segment.id}` } : {}),
          options: buildBase3DRendererOptions(),
          orientations: ['landscape', 'portrait']
        });

        if (isCancelled || rendererPrewarmRequestVersionRef.current !== requestVersion) {
          return;
        }

        last3DRendererPrewarmRouteKeyRef.current = routeKey;
        const activeKeys = applyRenderEntries(ensureResult?.data?.renders || []);
        scheduleRefresh(activeKeys);
      } catch (error) {
        if (!isCancelled) {
          console.error('CreatePage: Failed to ensure 3D renderer pre-render cache', error);
          setRenderer3DCacheState({
            landscape: {
              ...createEmpty3DRendererCacheEntry('landscape'),
              status: 'FAILED',
              error: error?.message || 'Failed to start 3D renderer pre-render.',
              message: error?.message || 'Failed to start 3D renderer pre-render.'
            },
            portrait: {
              ...createEmpty3DRendererCacheEntry('portrait'),
              status: 'FAILED',
              error: error?.message || 'Failed to start 3D renderer pre-render.',
              message: error?.message || 'Failed to start 3D renderer pre-render.'
            }
          });
        }
      }
    };

    startTimeoutId = setTimeout(ensurePrewarm, routeChanged ? 0 : 800);

    return () => {
      isCancelled = true;
      if (startTimeoutId) {
        clearTimeout(startTimeoutId);
      }
      stop3DRendererPrewarmPolling();
    };
  }, [
    buildBase3DRendererOptions,
    getSelected3DPolylineString,
    getSelected3DRendererRouteName,
    isAdmin,
    normalize3DRendererCacheEntry,
    northUpEnabled,
    resetRenderer3DCacheState,
    selectedActivity,
    selectedActivityIsZwift,
    selectedSegment?.segment?.id,
    stop3DRendererPrewarmPolling,
    threeDRenderSource
  ]);

  const buildBrowser3DDebugSummary = useCallback(async (captureResultOverride = null) => {
    if (threeDRenderSource !== 'browser') {
      return;
    }

    const debugCandidates = allMap3DDebugCandidatesRef.current;
    const cameraSearchSummary = browser3DCameraSearchSummaryRef.current;

    if (!debugCandidates.length && !cameraSearchSummary) {
      setBrowser3DDebugHtml('');
      return;
    }

    let captureResult = captureResultOverride;
    if (!captureResult && captureMapCallbackRef.current && is3DMapReadyRef.current) {
      captureResult = await captureMapCallbackRef.current();
    }

    if (!captureResult) {
      return;
    }

    const imageData = captureResult.dataUrl || await blobToDataUrl(captureResult.blob);
    if (!imageData) {
      return;
    }

    const renderOptions = buildSelected3DRendererRenderOptions();

    setBrowser3DDebugHtml(build3DMapDebugSummaryHtml({
      imageData,
      debugCandidates,
      cameraSearchSummary,
      options: renderOptions,
      defaultWidth: 1536,
      defaultHeight: 1024
    }));
  }, [
    blobToDataUrl,
    buildSelected3DRendererRenderOptions,
    threeDRenderSource
  ]);

  useEffect(() => {
    if (threeDRenderSource !== 'browser') {
      return;
    }
    if (!browser3DCameraSearchSummary) {
      return;
    }

    let isCancelled = false;
    const rebuildBrowserDebugHtml = async () => {
      try {
        await buildBrowser3DDebugSummary();
      } catch (error) {
        if (!isCancelled) {
          console.error('CreatePage: Failed to rebuild browser 3D debug HTML:', error);
        }
      }
    };

    void rebuildBrowserDebugHtml();

    return () => {
      isCancelled = true;
    };
  }, [browser3DCameraSearchSummary, buildBrowser3DDebugSummary, threeDRenderSource]);

  useEffect(() => {
    if (!browser2DCameraSearchSummary) {
      return;
    }

    const bestCandidate = browser2DCameraSearchSummary.bestCandidate
      || [...map2DDebugCandidates].sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity))[0]
      || null;

    setBrowser2DDebugHtml(build2DMapDebugSummaryHtml({
      imageData: bestCandidate?.thumbnailDataUrl || null,
      debugCandidates: map2DDebugCandidates,
      cameraSearchSummary: browser2DCameraSearchSummary,
      options: {
        width: browser2DCameraSearchSummary.searchConfig?.captureWidth,
        height: browser2DCameraSearchSummary.searchConfig?.captureHeight,
      },
    }));
  }, [browser2DCameraSearchSummary, map2DDebugCandidates]);

  const capture3DMapForCurrentSource = useCallback(async () => {
    if (threeDRenderSource === 'renderer' && isAdmin) {
      setIsRunning3DDebugPreview(false);
      captureMapCallbackRef.current = null;
      is3DMapReadyRef.current = false;
      setMap3DDebugCandidates([]);
      map3DDebugCandidatesRef.current = [];
      allMap3DDebugCandidatesRef.current = [];
      return capture3DMapVia3DRenderer();
    }

    const preparingText = t('createPage.preparing3D', 'Preparing 3D map...');
    setGenerationStatusText(preparingText);
    setMap3DProgressText(preparingText);
    setMap3DProgressRatio(0);
    setMap3DDebugCandidates([]);
    map3DDebugCandidatesRef.current = [];
    allMap3DDebugCandidatesRef.current = [];
    resetBrowser3DDebugState();
    setIsRunning3DDebugPreview(false);
    captureMapCallbackRef.current = null;
    is3DMapReadyRef.current = false;
    setIsPreparing3DMap(true);

    try {
      await waitFor3DPreparation();

      if (!captureMapCallbackRef.current) {
        throw new Error('3D map is not ready. Please try again.');
      }

      setGenerationStatusText(t('createPage.capturing3D', 'Capturing 3D map...'));
      const captureResult = await captureMapCallbackRef.current();
      reset3DPreparationState({ clearDebugCandidates: false });
      return captureResult;
    } catch (error) {
      reset3DPreparationState();
      throw error;
    }
  }, [
    capture3DMapVia3DRenderer,
    isAdmin,
    resetBrowser3DDebugState,
    reset3DPreparationState,
    t,
    threeDRenderSource,
    waitFor3DPreparation
  ]);

  useEffect(() => {
    if (!selectedStyleGroup) return;
    if (!force2DMapGeneration && !selectedZwiftActivityForces2D && mapDimension === '3d') {
      const template3D = selectedStyleGroup.templates.find(t => t.mapInputType === '3d') ||
        (selectedActivityHas3DWorld ? selectedStyleGroup.templates.find(t => t.mapInputType !== '3d') : null);
      if (template3D) {
        setSelectedTemplate(template3D);
      }
    } else {
      const template2D = selectedStyleGroup.templates.find(t => t.mapInputType !== '3d');
      if (template2D) {
        setSelectedTemplate(template2D);
      }
    }
  }, [force2DMapGeneration, mapDimension, selectedActivityHas3DWorld, selectedStyleGroup, selectedZwiftActivityForces2D]);

  useEffect(() => {
    preloadStyleGroupExamples(selectedStyleGroup);
  }, [preloadStyleGroupExamples, selectedStyleGroup]);

  useEffect(() => {
    resetRenderer3DRenderState();
    clearCaptured3DImage();
    resetBrowser3DDebugState();
  }, [
    clearCaptured3DImage,
    mapDimension,
    mapFormat,
    resetBrowser3DDebugState,
    resetRenderer3DRenderState,
    selectedActivity?.id,
    selectedSegment?.id,
    selectedStyleGroup?.name,
    threeDRenderMode
  ]);
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Load activities and usage on mount (guarded against StrictMode double effects)
  useEffect(() => {
    if (!user) {
      hasLoadedInitialDataRef.current = null;
      setAllActivities([]);
      setActivities([]);
      setActivityRefreshNotice(null);
      return;
    }

    if (hasLoadedInitialDataRef.current && hasLoadedInitialDataRef.current !== user.uid) {
      setAllActivities([]);
      setActivities([]);
      setActivityRefreshNotice(null);
    }

    if (hasLoadedInitialDataRef.current === user.uid) {
      return;
    }
    hasLoadedInitialDataRef.current = user.uid;

    loadActivities();
    loadTemplates();
    loadMonthlyUsage();
  }, [user]);

  // Load create-page settings from Firestore
  useEffect(() => {
    const loadCreatePageSettings = async () => {
      if (!user) return;

      // Load the user's map bearing preference (shared with auto-upload).
      // Absent field means auto search.
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const userSettings = userSnap.exists() ? (userSnap.data()?.settings || {}) : {};
        setUserPrefersNorthUp(userSettings?.mapBearingMode === 'north_up');
      } catch (error) {
        console.error('Error loading user map preferences:', error);
      }

      // Load global admin style order
      try {
        const settingsDocRef = doc(db, 'settings', 'mapGeneration');
        const settingsSnap = await getDoc(settingsDocRef);
        const settingsData = settingsSnap.exists() ? (settingsSnap.data() || {}) : {};
        setMapGenerationSettings(settingsData);
        setForce2DMapGeneration(settingsData?.force2DMapGeneration === true);
        if (settingsSnap.exists()) {
          if (Array.isArray(settingsData?.globalStyleOrder) && settingsData.globalStyleOrder.length > 0) {
            setGlobalStyleOrder(settingsData.globalStyleOrder);
          }
          if (Array.isArray(settingsData?.hiddenStyles)) {
            setHiddenStyles(settingsData.hiddenStyles);
          }
          const configured3DRouteLineWidth = (
            typeof settingsData?.routeLineWidth3d === 'number' && Number.isFinite(settingsData.routeLineWidth3d)
              ? settingsData.routeLineWidth3d
              : null
          );
          if (typeof configured3DRouteLineWidth === 'number' && Number.isFinite(configured3DRouteLineWidth)) {
            setThreeDLineWidth(Math.max(1, Math.round(configured3DRouteLineWidth)));
            setThreeDLineWidthConfigured(true);
          } else {
            setThreeDLineWidthConfigured(false);
          }
          setThreeDRenderMode(normalize3DRenderMode(settingsData?.threeDRenderMode));
        }
      } catch (error) {
        console.error('Error loading global style order:', error);
        setMapGenerationSettings({});
        setForce2DMapGeneration(false);
      }

      // Load tag categories
      try {
        const tagDoc = await getDoc(doc(db, 'settings', 'styleTags'));
        if (tagDoc.exists() && tagDoc.data().categories) {
          setTagCategories(tagDoc.data().categories);
          // Default to the first category tab
          const firstCategory = Object.keys(tagDoc.data().categories)[0];
          if (firstCategory) setActiveTagCategory(firstCategory);
        }
      } catch (error) {
        console.error('Error loading tag categories:', error);
      }
    };
    loadCreatePageSettings();
  }, [user]);

  const visibleStyleGroups = useMemo(() => {
    const groupsForUser = isAdmin
      ? [...styleGroups]
      : styleGroups.filter(g => !hiddenStyles.includes(g.name));

    // The "Zwift Map" style renders the literal Zwift world map, so it is
    // only offered when the selected activity is a Zwift one (admins too —
    // it produces nothing sensible for other activities).
    const groupsForActivity = selectedActivityIsZwift
      ? groupsForUser
      : groupsForUser.filter(g => !isZwiftMapStyleName(g.name));

    if (force2DMapGeneration) {
      return groupsForActivity.filter(group => group.has2D);
    }

    return groupsForActivity;
  }, [styleGroups, hiddenStyles, isAdmin, force2DMapGeneration, selectedActivityIsZwift]);

  const availableTagCategories = useMemo(() => {
    if (visibleStyleGroups.length === 0 || Object.keys(tagCategories).length === 0) {
      return {};
    }

    return Object.entries(tagCategories).reduce((acc, [category, tags]) => {
      const availableTags = tags.filter(tag =>
        visibleStyleGroups.some(group => Array.isArray(group.tags) && group.tags.includes(tag))
      );

      if (availableTags.length > 0) {
        acc[category] = availableTags;
      }

      return acc;
    }, {});
  }, [tagCategories, visibleStyleGroups]);

  // Sorted and paginated style groups based on admin defaults
  const sortedStyleGroups = useMemo(() => {
    if (styleGroups.length === 0) return [];

    // Filter by a single active tag tab
    let filtered = visibleStyleGroups;
    if (activeTag) {
      filtered = visibleStyleGroups.filter(g => Array.isArray(g.tags) && g.tags.includes(activeTag));
    }
    const sorted = filtered;

    const sortByOrderArray = (arr, orderArray) => {
      arr.sort((a, b) => {
        const aIdx = orderArray.indexOf(a.name);
        const bIdx = orderArray.indexOf(b.name);
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        return a.name.localeCompare(b.name);
      });
    };

    if (globalStyleOrder.length > 0) {
      sortByOrderArray(sorted, globalStyleOrder);
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }

    // For Zwift activities the dedicated "Zwift Map" style leads the list.
    if (selectedActivityIsZwift) {
      const zwiftMapIdx = sorted.findIndex(g => isZwiftMapStyleName(g.name));
      if (zwiftMapIdx > 0) {
        const [zwiftMapGroup] = sorted.splice(zwiftMapIdx, 1);
        sorted.unshift(zwiftMapGroup);
      }
    }

    return sorted;
  }, [styleGroups, globalStyleOrder, visibleStyleGroups, activeTag, selectedActivityIsZwift]);

  const paginatedStyles = useMemo(() => {
    const startIdx = currentStylePage * STYLES_PER_PAGE;
    return sortedStyleGroups.slice(startIdx, startIdx + STYLES_PER_PAGE);
  }, [sortedStyleGroups, currentStylePage]);

  const totalStylePages = Math.ceil(sortedStyleGroups.length / STYLES_PER_PAGE);

  // Unlocked event styles pinned at the top of the picker (independent of the tag filter),
  // soonest-expiring first, max 3 — a reminder to use them before they lapse.
  const eventStyleGroups = useMemo(() => {
    const withEvent = visibleStyleGroups
      .map((group) => {
        const eventTemplates = (group.templates || []).filter((t) => t.eventId);
        if (eventTemplates.length === 0) return null;
        const ends = eventTemplates
          .map((t) => (typeof t.eventEndsAtMs === 'number' ? t.eventEndsAtMs : null))
          .filter((v) => v != null);
        return { group, endsAtMs: ends.length > 0 ? Math.min(...ends) : null };
      })
      .filter(Boolean);
    withEvent.sort((a, b) => (a.endsAtMs || Infinity) - (b.endsAtMs || Infinity));
    return withEvent.slice(0, 3);
  }, [visibleStyleGroups]);

  useEffect(() => {
    if (
      selectedStyleGroup &&
      !sortedStyleGroups.some(group => group.name === selectedStyleGroup.name) &&
      !eventStyleGroups.some(entry => entry.group.name === selectedStyleGroup.name)
    ) {
      setSelectedStyleGroup(null);
      setSelectedTemplate(null);
    }
  }, [selectedStyleGroup, sortedStyleGroups, eventStyleGroups]);

  useEffect(() => {
    const categoryNames = Object.keys(availableTagCategories);
    if (categoryNames.length === 0) {
      if (activeTagCategory !== null) setActiveTagCategory(null);
      if (activeTag !== null) setActiveTag(null);
      return;
    }

    if (!activeTagCategory || !availableTagCategories[activeTagCategory]) {
      setActiveTagCategory(categoryNames[0]);
      if (activeTag !== null) setActiveTag(null);
      return;
    }

    if (activeTag && !availableTagCategories[activeTagCategory]?.includes(activeTag)) {
      setActiveTag(null);
    }
  }, [availableTagCategories, activeTagCategory, activeTag]);

  // Reset style page when the active tag filter changes or the style list
  // gains/loses the Zwift Map entry on activity switch
  useEffect(() => {
    setCurrentStylePage(0);
  }, [activeTag, selectedActivityIsZwift]);

  // Reload templates when admin status changes
  useEffect(() => {
    if (user) {
      console.log('Admin status changed, reloading templates. isAdmin:', isAdmin);
      loadTemplates();
    }
  }, [isAdmin, user]); // Add user to dependencies for consistency

  // Handle direct activity link when activityId is present
  useEffect(() => {
    if (activityId && user) {
      // Load the specific activity
      loadTargetActivity(activityId);

      // Also load templates if not already loaded
      if (promptTemplates.length === 0) {
        loadTemplates();
      }

      // Load monthly usage if not already loaded
      if (!monthlyUsage.remaining) {
        loadMonthlyUsage();
      }
    }
  }, [activityId, user]);

  // Mock economy: the spendable balance is the mock diamond balance when enabled,
  // otherwise the real getUserMonthlyUsage remaining value.
  const effectiveRemaining = monthlyUsage.remaining;

  // Clear error when diamonds become available
  useEffect(() => {
    if (effectiveRemaining > 0 && error === 'noDiamonds') {
      setError('');
    }
  }, [effectiveRemaining, error]);

  const loadMonthlyUsage = async () => {
    try {
      const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
      const result = await getUserMonthlyUsageFunc();
      setMonthlyUsage(result.data);
      // Pass data to parent to update navigation bar counter
      if (onCreditsUpdated) {
        onCreditsUpdated(result.data);
      }
    } catch (error) {
      console.error('Error loading monthly usage:', error);
    }
  };

  const handleStravaReconnect = () => {
    // Store state for OAuth flow
    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('stravaOAuthState', state);

    // Store the current activity ID if present, so user can resume after reconnecting
    if (activityId) {
      localStorage.setItem('pendingActivityId', activityId);
    }

    // Redirect to Strava OAuth
    const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=force&state=${state}`;
    window.location.href = consentUrl;
  };

  const applyActivityList = (nextActivities) => {
    const normalizedActivities = Array.isArray(nextActivities) ? nextActivities : [];
    setAllActivities(normalizedActivities);
    setActivities(normalizedActivities.filter(act => act.map && act.map.summary_polyline));
  };

  const loadActivities = async () => {
    if (!user?.uid) return;

    setIsLoadingActivities(true);
    setStravaError(null); // Clear any previous error
    setActivityRefreshNotice(null);

    const cached = readCachedStravaActivities(user.uid);
    const cachedActivities = cached?.activities || [];
    const hasCachedActivities = cachedActivities.length > 0;
    if (hasCachedActivities) {
      applyActivityList(cachedActivities);
    }

    try {
      const getActivitiesFunc = httpsCallable(functions, 'getStravaActivities');
      const result = await getActivitiesFunc();

      const freshActivities = result.data && Array.isArray(result.data) ? result.data : [];
      const newActivityCount = countNewActivities(freshActivities, cachedActivities);
      writeCachedStravaActivities(user.uid, freshActivities);

      applyActivityList(freshActivities);

      if (hasCachedActivities && newActivityCount > 0) {
        setActivityRefreshNotice({
          type: 'success',
          message: t(
            'createPage.activitiesRefreshFound',
            `Found ${newActivityCount} new ${newActivityCount === 1 ? 'activity' : 'activities'}.`,
            { count: newActivityCount }
          )
        });
      }

      if (newActivityCount > 0) {
        setCurrentPage(0);
      }
    } catch (error) {
      console.error('Error loading activities:', error);

      // Check if this is a Strava permission error
      let errorMessage = 'Failed to load activities';
      let showReconnectButton = false;

      // Firebase wraps the error, so check the message directly
      if (error.message) {
        try {
          // Try to parse the error message as JSON
          const errorDetails = JSON.parse(error.message);
          if (errorDetails.needsReconnect) {
            errorMessage = errorDetails.userMessage;
            showReconnectButton = true;

            // Log the raw Strava response for debugging
            console.error('Strava API Response:', errorDetails.stravaResponse);
          }
        } catch (parseError) {
          // If message isn't JSON, check if it's a permission error
          if (error.code === 'permission-denied') {
            errorMessage = error.message || 'Failed to load activities';
          }
        }
      }

      if (hasCachedActivities) {
        setActivityRefreshNotice({
          type: showReconnectButton ? 'warning' : 'error',
          message: showReconnectButton
            ? t('createPage.activitiesRefreshReconnect', 'Strava needs reconnecting. Showing saved activities for now.')
            : t('createPage.activitiesRefreshFailed', 'Could not refresh activities. Showing saved activities for now.'),
          action: showReconnectButton ? 'reconnect' : null
        });
        setStravaError(null);
      } else {
        // Set error with reconnect option if needed
        if (showReconnectButton) {
          setStravaError(
            <div>
              <p>{errorMessage}</p>
              <details style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                <summary>Technical Details</summary>
                <pre style={{
                  backgroundColor: '#f5f0e8',
                  padding: '10px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {JSON.stringify(JSON.parse(error.message).stravaResponse, null, 2)}
                </pre>
              </details>
              <button
                onClick={handleStravaReconnect}
                style={{
                  marginTop: '15px',
                  padding: '10px 20px',
                  backgroundColor: '#FC4C02',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Reconnect to Strava
              </button>
            </div>
          );
        } else {
          setError(errorMessage);
        }

        setAllActivities([]);
        setActivities([]);
      }
    } finally {
      setIsLoadingActivities(false);
    }
  };

  loadActivitiesRef.current = loadActivities;

  useEffect(() => {
    const handleCreatePageActivityRefresh = () => {
      if (!loadActivitiesRef.current) return;
      loadActivitiesRef.current();
    };

    window.addEventListener(CREATE_PAGE_REFRESH_ACTIVITIES_EVENT, handleCreatePageActivityRefresh);
    return () => {
      window.removeEventListener(CREATE_PAGE_REFRESH_ACTIVITIES_EVENT, handleCreatePageActivityRefresh);
    };
  }, []);

  // Fetch a specific activity by ID (for direct links)
  const loadTargetActivity = async (targetActivityId) => {
    setIsLoadingTargetActivity(true);
    setTargetActivityError(null);

    try {
      // Fetch the specific activity by ID
      const getActivityByIdFunc = httpsCallable(functions, 'getStravaActivityById');
      const result = await getActivityByIdFunc({ activityId: targetActivityId });

      if (result.data) {
        // Check if activity has GPS data
        if (!result.data.map || !result.data.map.summary_polyline) {
          setTargetActivityError('This activity does not have GPS data and cannot be used for map generation.');
          return;
        }

        // Set as selected activity
        setSelectedActivity(result.data);

        // Load segment efforts for this activity
        loadSegmentEfforts(targetActivityId.toString());

        // Add to activities list if not already there
        setAllActivities(prevActivities => {
          const exists = prevActivities.some(a => a.id === result.data.id);
          if (!exists) {
            return [result.data, ...prevActivities];
          }
          return prevActivities;
        });

        // Track that user arrived via direct link
        posthog?.capture('activity_selected_via_link', {
          activity_id: targetActivityId,
          activity_name: result.data.name,
          activity_type: result.data.type,
          page: 'create'
        });

        return result.data;
      } else {
        setTargetActivityError('Activity not found or not accessible.');
      }
    } catch (error) {
      console.error('Error loading target activity:', error);

      // Parse error message for better user feedback
      const errorMessage = error.message || '';
      if (errorMessage.includes('not found') || errorMessage.includes('not have access')) {
        setTargetActivityError('This activity was not found or you don\'t have access to it.');
      } else if (errorMessage.includes('connect') || errorMessage.includes('Strava')) {
        setTargetActivityError('Please connect your Strava account to view this activity.');
      } else {
        setTargetActivityError('Failed to load activity. Please try again.');
      }
    } finally {
      setIsLoadingTargetActivity(false);
    }
  };

  // Paginated activities - always show 9 (3x3 grid) on both desktop and mobile
  const ACTIVITIES_PER_PAGE = 9;
  const paginatedActivities = useMemo(() => {
    const startIdx = currentPage * ACTIVITIES_PER_PAGE;
    return allActivities.slice(startIdx, startIdx + ACTIVITIES_PER_PAGE);
  }, [allActivities, currentPage]);

  const activityPages = Math.ceil(allActivities.length / ACTIVITIES_PER_PAGE);
  // Add an extra page for the Strava ID input when there are activities
  const totalPages = allActivities.length > 0 ? activityPages + 1 : 0;
  const isOnStravaIdPage = allActivities.length > 0 && currentPage === activityPages;

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleStravaIdSubmit = () => {
    const input = stravaIdInput.trim();
    if (!input) {
      setStravaIdError(t('createPage.stravaIdEmpty', 'Please enter a Strava activity ID or link.'));
      return;
    }
    let stravaId = input;
    const urlMatch = input.match(/strava\.com\/activities\/(\d+)/);
    if (urlMatch) {
      stravaId = urlMatch[1];
    } else if (!/^\d+$/.test(input)) {
      setStravaIdError(t('createPage.stravaIdInvalid', 'Please enter a valid Strava activity ID or URL.'));
      return;
    }
    setStravaIdError('');
    navigate(`/create/${stravaId}`);
  };

  const handleActivityClick = (activity) => {
    // Don't allow selection of activities without GPS data
    if (!activity.map || !activity.map.summary_polyline) {
      return;
    }

    // If we came from a direct activity link and click on the selected activity, navigate to /create to see all
    if (activityId && selectedActivity?.id === activity.id) {
      navigate('/create');
      return;
    }

    // Toggle selection if clicking the same activity (deselect to go back to grid)
    if (selectedActivity?.id === activity.id) {
      reset3DPreparationState({ rejectPending: true });
      setGenerationStatusText('');
      setSelectedActivity(null);
      setSegmentEfforts([]);
      setSelectedSegment(null);
      setIsSegmentsExpanded(false);
      posthog?.capture('activity_deselected', {
        activity_id: activity.id,
        activity_name: activity.name,
        page: 'create'
      });
    } else {
      reset3DPreparationState({ rejectPending: true });
      setGenerationStatusText('');
      setSelectedActivity(activity);
      setMapDimension(force2DMapGeneration ? '2d' : '3d'); // Reset when activity changes
      setSelectedSegment(null); // Reset selected segment
      setIsSegmentsExpanded(false); // Collapse segments for new activity
      setCustomTitle(''); // Reset custom title for new activity
      loadSegmentEfforts(activity.id.toString()); // Load segment efforts
      posthog?.capture('activity_selected', {
        activity_id: activity.id,
        activity_name: activity.name,
        activity_type: activity.type,
        distance_km: (activity.distance / 1000).toFixed(1),
        page: 'create'
      });
    }
  };

  const loadSegmentEfforts = async (activityId) => {
    setIsLoadingSegments(true);
    setSegmentEfforts([]);

    try {
      const getActivityFunc = httpsCallable(functions, 'getStravaActivityById');
      const result = await getActivityFunc({
        activityId: activityId,
        includeSegmentEfforts: true
      });

      if (result.data && result.data.segment_efforts) {
        setSegmentEfforts(result.data.segment_efforts);
        console.log('Loaded segment efforts:', result.data.segment_efforts);
      }
    } catch (error) {
      console.error('Error loading segment efforts:', error);
      // Don't show error to user - segments are optional
    } finally {
      setIsLoadingSegments(false);
    }
  };

  const handleSegmentSelect = async (segment) => {
    if (!segment) {
      reset3DPreparationState({ rejectPending: true });
      setGenerationStatusText('');
      setSelectedSegment(null);
      setIsLoadingSegmentPolyline(false);
      return;
    }

    // Set segment immediately for UI feedback
    reset3DPreparationState({ rejectPending: true });
    setGenerationStatusText('');
    setSelectedSegment(segment);

    posthog?.capture('segment_selected', {
      segment_id: segment.id,
      segment_name: segment.segment?.name || segment.name,
      activity_id: selectedActivity?.id,
      page: 'create'
    });

    // Check if we already have the polyline
    if (segment.segment?.map?.polyline) {
      console.log('Segment already has polyline');
      setIsLoadingSegmentPolyline(false);
      return;
    }

    // Fetch full segment details to get polyline
    setIsLoadingSegmentPolyline(true);
    try {
      const getSegmentFunc = httpsCallable(functions, 'getStravaSegmentById');
      const result = await getSegmentFunc({ segmentId: segment.segment.id });

      if (result.data && result.data.map) {
        console.log('Fetched segment polyline:', result.data.map.polyline?.substring(0, 50) + '...');
        // Update the segment with full details including polyline
        setSelectedSegment(prev => ({
          ...prev,
          segment: {
            ...prev.segment,
            map: result.data.map
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching segment details:', error);
      // Don't show error to user - map will just show full activity
    } finally {
      setIsLoadingSegmentPolyline(false);
    }
  };

  const loadTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
      const result = await getTemplatesFunc();

      if (result.data && Array.isArray(result.data.templates)) {
        // Filter out admin-only templates unless user is admin
        // Exception: For free challenge generations, include the challenge style even if admin-only
        const decodedChallengeStyle = challengeStyle ? decodeURIComponent(challengeStyle) : null;
        const userTemplates = result.data.templates.filter(t => {
          // Always include if not admin-only
          if (!t.adminOnly) return true;
          // Include if user is admin
          if (isAdmin) return true;
          // Event-exclusive styles: the server only returns these to admins/claimers (within the
          // event window), so trust them even though they're marked admin-only.
          if (t.eventId) return true;
          // For free challenge generations, include the specific challenge style template
          if (isFreeGeneration && decodedChallengeStyle) {
            // Match base name (remove "3D" suffix and emoji prefix if present)
            let baseName = t.name;
            if (baseName.includes(' 3D')) {
              baseName = baseName.replace(' 3D', '').trim();
            }
            if (baseName.endsWith('3D')) {
              baseName = baseName.slice(0, -2).trim();
            }
            // Remove emoji prefix (emoji followed by space at start of name)
            const nameWithoutEmoji = baseName.replace(/^[\p{Emoji}\p{Emoji_Modifier}\p{Emoji_Component}\p{Emoji_Modifier_Base}\p{Emoji_Presentation}]+\s*/u, '').trim();
            return baseName === decodedChallengeStyle ||
                   t.name === decodedChallengeStyle ||
                   nameWithoutEmoji === decodedChallengeStyle;
          }
          return false;
        });
        setPromptTemplates(userTemplates);

        console.log('Loaded templates:', {
          isAdmin,
          isFreeGeneration,
          challengeStyle: decodedChallengeStyle,
          totalTemplates: result.data.templates.length,
          adminOnlyTemplates: result.data.templates.filter(t => t.adminOnly).length,
          visibleTemplates: userTemplates.length,
          templates: userTemplates
        });
        
        // Group templates by base style name (remove "3D" suffix and trim)
        const groups = {};
        userTemplates.forEach(template => {
          // Remove "3D" suffix to group variants together
          let baseName = template.name;
          if (baseName.includes(' 3D')) {
            baseName = baseName.replace(' 3D', '').trim();
          }
          if (baseName.endsWith('3D')) {
            baseName = baseName.slice(0, -2).trim();
          }
          
          if (!groups[baseName]) {
            groups[baseName] = {
              name: baseName,
              templates: [],
              has3D: false,
              has2D: false,
              logoUrl: null,
              tags: []
            };
          }

          groups[baseName].templates.push(template);

          if (template.mapInputType === '3d' || template.name.includes('3D')) {
            groups[baseName].has3D = true;
          } else {
            groups[baseName].has2D = true;
          }

          // Union tags from all variants into the group
          if (template.tags && template.tags.length > 0) {
            template.tags.forEach(tag => {
              if (!groups[baseName].tags.includes(tag)) {
                groups[baseName].tags.push(tag);
              }
            });
          }

          // Use logo from template (prioritize 2D variant's logo)
          if (template.logoUrl) {
            if (!groups[baseName].logoUrl || template.mapInputType !== '3d') {
              groups[baseName].logoUrl = template.logoUrl;
            }
          }
        });
        
        const groupsArray = Object.values(groups);
        setStyleGroups(groupsArray);

      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleStyleSelect = useCallback((styleGroup) => {
    reset3DPreparationState({ rejectPending: true });
    setGenerationStatusText('');
    setSelectedStyleGroup(styleGroup);
    const targetMapDimension = (force2DMapGeneration || selectedZwiftActivityForces2D) ? '2d' : mapDimension;
    // Auto-select 2D variant if available
    const templateForCurrentDimension = styleGroup.templates.find(t =>
      targetMapDimension === '3d' ? t.mapInputType === '3d' : t.mapInputType !== '3d'
    ) || (targetMapDimension === '3d' && selectedActivityHas3DWorld
      ? styleGroup.templates.find(t => t.mapInputType !== '3d')
      : null);

    let selectedTemplateToUse;
    if (templateForCurrentDimension) {
      setSelectedTemplate(templateForCurrentDimension);
      selectedTemplateToUse = templateForCurrentDimension;
    } else {
      const fallback = styleGroup.templates.find(t => t.mapInputType !== '3d') || styleGroup.templates[0];
      setSelectedTemplate(fallback);
      setMapDimension((force2DMapGeneration || selectedZwiftActivityForces2D) ? '2d' : (fallback?.mapInputType === '3d' ? '3d' : '2d'));
      selectedTemplateToUse = fallback;
    }

    // Track template selection
    posthog?.capture('prompt_template_selected', {
      template_id: selectedTemplateToUse?.id,
      template_name: selectedTemplateToUse?.name,
      style_group: styleGroup.name,
      map_dimension: targetMapDimension,
      source_page: 'create'
    });
  }, [force2DMapGeneration, mapDimension, posthog, reset3DPreparationState, selectedActivityHas3DWorld, selectedZwiftActivityForces2D]);

  // Auto-select the style (and 2D/3D dimension) passed in the URL, e.g. from the
  // /maps style gallery (?style=…&dim=…) or a free challenge link. Applied once
  // via the ref guard so it never overrides a style the user later picks.
  useEffect(() => {
    if (hasAppliedUrlStyleRef.current) return;
    if (!challengeStyle || !styleGroups.length) return;
    hasAppliedUrlStyleRef.current = true;

    if (styleDimension === '2d' || styleDimension === '3d') {
      setMapDimension(styleDimension);
    }

    const decodedStyle = decodeURIComponent(challengeStyle);
    // Helper to remove emoji prefix from style name
    const removeEmojiPrefix = (name) => name.replace(/^[\p{Emoji}\p{Emoji_Modifier}\p{Emoji_Component}\p{Emoji_Modifier_Base}\p{Emoji_Presentation}]+\s*/u, '').trim();

    // Try exact match first, then decoded match, then match without emoji prefix
    const match = styleGroups.find(g => g.name === challengeStyle) ||
                  styleGroups.find(g => g.name === decodedStyle) ||
                  styleGroups.find(g => removeEmojiPrefix(g.name) === decodedStyle);
    if (match) {
      handleStyleSelect(match);
    } else {
      console.warn('[CreatePage] No matching style found for URL param:', challengeStyle);
    }
  }, [challengeStyle, styleDimension, styleGroups, handleStyleSelect]);

  // Pre-select the "Zwift Map" style when a Zwift activity is chosen. Applied
  // once per activity via the ref guard, so the user can still switch styles.
  const zwiftMapAutoSelectedForRef = useRef(null);
  useEffect(() => {
    if (restyleIntent) return;
    if (!selectedActivityIsZwift) {
      zwiftMapAutoSelectedForRef.current = null;
      return;
    }
    const activityKey = selectedActivity?.id;
    if (!activityKey || zwiftMapAutoSelectedForRef.current === activityKey) return;
    if (!styleGroups.length) return;

    const zwiftMapGroup = styleGroups.find(g => isZwiftMapStyleName(g.name));
    if (!zwiftMapGroup) return;

    zwiftMapAutoSelectedForRef.current = activityKey;
    if (selectedStyleGroup?.name !== zwiftMapGroup.name) {
      handleStyleSelect(zwiftMapGroup);
    }
  }, [restyleIntent, selectedActivityIsZwift, selectedActivity, styleGroups, selectedStyleGroup, handleStyleSelect]);

  // Restyle links carry the dimension of the map the user was viewing on the
  // map page (?dim=2d|3d); apply it once as the starting dimension.
  const restyleDimAppliedRef = useRef(false);
  useEffect(() => {
    if (!restyleIntent || restyleDimAppliedRef.current) return;
    if (styleDimension !== '2d' && styleDimension !== '3d') return;
    restyleDimAppliedRef.current = true;
    setMapDimension(styleDimension);
  }, [restyleIntent, styleDimension]);

  const handleDimensionChange = (dimension) => {
    if ((force2DMapGeneration || selectedZwiftActivityForces2D) && dimension === '3d') {
      return;
    }
    if (dimension === mapDimension) {
      return;
    }
    reset3DPreparationState({ rejectPending: true });
    setIsRunning3DDebugPreview(false);
    setGenerationStatusText('');
    setMapDimension(dimension);
    setMap3DProgressText('');
    setMap3DDebugCandidates([]);
    map3DDebugCandidatesRef.current = [];
    allMap3DDebugCandidatesRef.current = [];
    resetBrowser3DDebugState();
  };

  useEffect(() => {
    if (!selectedZwiftActivityForces2D || mapDimension === '2d') {
      return;
    }

    reset3DPreparationState({ rejectPending: true });
    setIsRunning3DDebugPreview(false);
    setGenerationStatusText('');
    setMapDimension('2d');
    setMap3DProgressText('');
    setMap3DDebugCandidates([]);
    map3DDebugCandidatesRef.current = [];
    allMap3DDebugCandidatesRef.current = [];
    resetBrowser3DDebugState();
  }, [mapDimension, reset3DPreparationState, resetBrowser3DDebugState, selectedZwiftActivityForces2D]);

  const reset3DSearchControlsOutputs = useCallback(() => {
    resetRenderer3DRenderState();
    resetBrowser3DDebugState();
    clearCaptured3DImage();
    setMap3DDebugCandidates([]);
    map3DDebugCandidatesRef.current = [];
    allMap3DDebugCandidatesRef.current = [];
  }, [
    clearCaptured3DImage,
    resetBrowser3DDebugState,
    resetRenderer3DRenderState
  ]);

  const handle3DTightenCandidateCountChange = (nextValue) => {
    const normalizedCount = Math.max(1, Math.min(10, Math.round(Number(nextValue) || 1)));
    setThreeDTightenCandidateCount(normalizedCount);
    reset3DSearchControlsOutputs();
  };

  const handle3DCoarseSearchCountChange = (nextValue) => {
    const normalizedCount = Math.max(1, Math.min(36, Math.round(Number(nextValue) || 1)));
    setThreeDCoarseSearchCount(normalizedCount);
    reset3DSearchControlsOutputs();
  };

  const handle3DScoreMultiplierChange = (setter, nextValue) => {
    const normalizedValue = Math.max(0, Math.min(10, Number(nextValue) || 0));
    setter(normalizedValue);
    reset3DSearchControlsOutputs();
  };

  const handleShow3DDebugMaps = async () => {
    if (threeDRenderSource !== 'browser') return;
    if (force2DMapGeneration || selectedActivityIsZwift || mapDimension !== '3d' || !selectedStyleGroup?.has3D) return;
    if (isGenerating || isRunning3DDebugPreview) return;

    const preparingText = t('createPage.preparing3D', 'Preparing 3D map...');

    setError('');
    captureMapCallbackRef.current = null;
    is3DMapReadyRef.current = false;
    setMap3DProgressText(preparingText);
    setMap3DProgressRatio(0);
    setMap3DDebugCandidates([]);
    map3DDebugCandidatesRef.current = [];
    allMap3DDebugCandidatesRef.current = [];
    setIsShowing3DDebugRenderer(true);
    setIsRunning3DDebugPreview(true);
    setIsPreparing3DMap(true);

    try {
      await waitFor3DPreparation(300000);
      await buildBrowser3DDebugSummary();
      setMap3DProgressText(t('createPage.ready3D', '3D map ready'));
    } catch (previewError) {
      console.error('Error preparing 3D debug maps:', previewError);
      setError(previewError?.message || t('createPage.failed3D', 'Failed to prepare 3D map.'));
    } finally {
      setIsPreparing3DMap(false);
      setIsRunning3DDebugPreview(false);
    }
  };

  const handleRenderMapOnly = async () => {
    if (force2DMapGeneration || selectedActivityIsZwift || mapDimension !== '3d' || !selectedActivity || !selectedStyleGroup?.has3D) return;
    if (isGenerating || isRunning3DDebugPreview || isRendering3DOnly) return;

    setError('');
    setIsRendering3DOnly(true);

    try {
      const captureResult = await capture3DMapForCurrentSource();
      if (!captureResult?.blob) {
        throw new Error('Failed to render 3D map preview.');
      }

      if (threeDRenderSource === 'browser') {
        await buildBrowser3DDebugSummary(captureResult);
      }

      const nextObjectUrl = URL.createObjectURL(captureResult.blob);
      setCaptured3DImage((previousImage) => {
        if (previousImage?.objectUrl) {
          URL.revokeObjectURL(previousImage.objectUrl);
        }

        return {
          objectUrl: nextObjectUrl,
          mimeType: captureResult.mimeType || captureResult.blob.type || 'image/png',
          size: captureResult.blob.size,
          source: threeDRenderSource,
          createdAt: Date.now()
        };
      });
      setGenerationStatusText('3D render preview ready.');
    } catch (renderError) {
      console.error('Error rendering 3D map preview:', renderError);
      setError(renderError?.message || 'Error rendering 3D map preview.');
      setGenerationStatusText('');
    } finally {
      setIsRendering3DOnly(false);
    }
  };

  const handleGenerate = async () => {
    const effectiveMapDimension = (force2DMapGeneration || selectedZwiftActivityForces2D) ? '2d' : mapDimension;
    const selectedTemplateForGeneration = effectiveMapDimension === '3d'
      ? (selectedStyleGroup?.templates.find(t => t.mapInputType === '3d') || selectedTemplate)
      : (selectedStyleGroup?.templates.find(t => t.mapInputType !== '3d') || selectedTemplate);

    if (!selectedActivity || !selectedTemplateForGeneration) return;
    if (isGenerating) return; // Prevent double submission

    // Check if user has diamonds remaining (skip for free challenge
    // generations and free render-only Zwift maps)
    if (!isFreeGeneration && !isFreeZwiftRender && effectiveRemaining <= 0) {
      setError('noDiamonds'); // Use special error code for custom message
      posthog?.capture('out_of_credits', {
        context: 'generation_attempted',
        page: 'create',
        activity_id: selectedActivity?.id
      });
      return;
    }

    // Track generation started
      posthog?.capture('map_generation_started', {
        activity_id: selectedActivity.id.toString(),
        activity_name: selectedActivity.name,
        template_id: selectedTemplateForGeneration.id,
        template_name: selectedTemplateForGeneration.name,
        map_dimension: effectiveMapDimension,
        map_format: mapFormat,
        source_page: 'create',
        ...(effectiveMapDimension === '3d' ? { three_d_render_source: effectiveThreeDRenderSource } : {}),
        ...(effectiveMapDimension === '3d' ? { three_d_render_mode: selectedActivityHas3DWorld ? 'renderer' : threeDRenderMode } : {}),
        ...(force2DMapGeneration ? { force_2d_map_generation: true } : {}),
        ...(selectedActivityIsZwift ? { zwift_custom_map: true, zwift_world: selectedZwiftWorld?.slug } : {}),
        is_segment_generation: !!selectedSegment?.segment?.id,
        segment_id: selectedSegment?.segment?.id,
        segment_name: selectedSegment?.segment?.name
    });

    // Set loading state immediately
    setIsGenerating(true);
    setError('');
    setGenerationStatusText(t('createPage.generating', 'Generating...'));

    // If 3D map, capture it first
    let captured3DMapPath = null;
    const isBackendRenderer3DGeneration =
      effectiveMapDimension === '3d' && effectiveThreeDRenderSource === 'renderer';
    const selectedRenderer3DCacheEntry = isBackendRenderer3DGeneration ? getSelected3DRendererCacheEntry() : null;
    if (effectiveMapDimension === '3d' && !isBackendRenderer3DGeneration) {
      try {
        const captureResult = await capture3DMapForCurrentSource();

        if (!captureResult || !captureResult.blob) {
          setError('Failed to capture 3D map. Please try again.');
          setIsGenerating(false);
          return;
        }

        if (threeDRenderSource === 'browser') {
          await buildBrowser3DDebugSummary(captureResult);
        }

        // Upload to Firebase Storage
        const timestamp = Date.now();
        const pathExtension = captureResult.mimeType === 'image/jpeg' ? 'jpg' : 'png';
        const segmentSuffix = selectedSegment?.segment?.id ? `_seg${selectedSegment.segment.id}` : '';
        const storagePath = `composite-maps/${user.uid}/captured-3d/${selectedActivity.id}${segmentSuffix}_${timestamp}.${pathExtension}`;
        const storageRef = ref(storage, storagePath);

        setGenerationStatusText(t('createPage.uploading3D', 'Uploading 3D map...'));
        await uploadBytes(storageRef, captureResult.blob, {
          contentType: captureResult.mimeType || 'image/png'
        });

        captured3DMapPath = storagePath;
        console.log('3D map captured and uploaded:', storagePath);
      } catch (error) {
        console.error('Error capturing 3D map:', error);
        setError('Error capturing 3D map. Please try again.');
        setIsGenerating(false);
        setGenerationStatusText('');
        reset3DPreparationState();
        return;
      }
    }
    
    try {
      setGenerationStatusText(t('createPage.generating', 'Generating...'));
      const requestImageGenerationFunc = httpsCallable(functions, 'requestImageGeneration');

      // Determine if we're generating for a segment or the full activity
      // We only need to check if a segment is selected - backend will fetch the polyline
      const isSegmentGeneration = selectedSegment && selectedSegment.segment?.id;

      const payload = {
        StravaId: selectedActivity.id.toString(),
        promptTemplateId: selectedTemplateForGeneration.id,
        gpxDataSource: 'polyline',
        mapInputType: effectiveMapDimension,
        mapOrientation: mapFormat,
        ...(customTitle.trim() && { customTitle: customTitle.trim() }),
        ...(distanceUnit === 'imperial' && { distanceUnit: 'imperial' }),
        ...(northUpEnabled && { mapBearingMode: 'north_up' }),
        ...(selectedActivityIsZwift && { useZwiftCustomMap: true }),
        ...(isFreeZwiftRender && { renderOnly: true }),
        ...(isFreeGeneration && { isChallengeGeneration: true, challengeId }),
      };

      // For segment generation, send segment IDs - backend will fetch stats securely
      // For full activity, don't send stats - backend fetches from Strava
      if (isSegmentGeneration) {
        payload.segmentId = selectedSegment.segment.id;
        payload.segmentEffortId = selectedSegment.id; // The effort ID for this user's attempt
        payload.isSegmentGeneration = true;
        console.log('Segment generation payload:', {
          segmentId: payload.segmentId,
          segmentEffortId: payload.segmentEffortId,
          isSegmentGeneration: payload.isSegmentGeneration
        });
      }

      if (captured3DMapPath) {
        payload.captured3DMapPath = captured3DMapPath;
        payload.use3DMap = true;
      }

      if (isBackendRenderer3DGeneration) {
        payload.use3DMap = true;
        payload.threeDRenderSource = 'renderer';
        payload.renderer3DRenderOptions = buildSelected3DRendererRenderOptions();
        if (selectedRenderer3DCacheEntry?.cacheKey) {
          payload.renderer3DRenderCacheKey = selectedRenderer3DCacheEntry.cacheKey;
          payload.renderer3DRenderCacheStatus = selectedRenderer3DCacheEntry.status;
        }
      } else if (effectiveMapDimension === '3d') {
        payload.threeDRenderSource = effectiveThreeDRenderSource;
      }

      // Watopia free Zwift renders produce both maps (3D primary + 2D
      // secondary); the backend drives the 3D render, no browser prep needed.
      if (isFreeZwiftRender && selectedActivityHas3DWorld) {
        payload.use3DMap = true;
        payload.threeDRenderSource = 'renderer';
      }

      console.log('Full generation payload:', payload);
      const result = await requestImageGenerationFunc(payload);

      // Track successful generation
      posthog?.capture('map_generation_request_success', {
        request_id: result.data.requestId,
        activity_id: selectedActivity.id.toString(),
        template_id: selectedTemplateForGeneration.id,
        map_dimension: effectiveMapDimension,
        ...(effectiveMapDimension === '3d' ? { three_d_render_source: effectiveThreeDRenderSource } : {}),
        ...(effectiveMapDimension === '3d' ? { three_d_render_mode: selectedActivityHas3DWorld ? 'renderer' : threeDRenderMode } : {}),
        ...(force2DMapGeneration ? { force_2d_map_generation: true } : {}),
        ...(selectedActivityIsZwift ? { zwift_custom_map: true, zwift_world: selectedZwiftWorld?.slug } : {}),
        source_page: 'create'
      });

      // Navigate to manage page
      navigate(`/manage-post/${result.data.requestId}`);
    } catch (error) {
      console.error('Error generating map:', error);
      // Check if it's a resource-exhausted error (no diamonds left)
      if (error.code === 'functions/resource-exhausted' || error.message?.includes('limit reached')) {
        setError('noDiamonds'); // Use special error code for custom message
        posthog?.capture('out_of_credits', {
          context: 'generation_error',
          page: 'create',
          activity_id: selectedActivity?.id,
          error_code: error.code
        });
      } else if (error.code === 'functions/already-exists' || error.message?.includes('already claimed')) {
        // Free challenge generation already claimed for this activity
        setError('alreadyClaimed');
        posthog?.capture('free_generation_already_claimed', {
          context: 'create_page',
          activity_id: selectedActivity?.id,
          challenge_id: challengeId
        });
      } else if (error.code === 'functions/permission-denied') {
        // Not eligible for free challenge generation
        setError(error.message || 'You are not eligible for a free map for this activity.');
        posthog?.capture('free_generation_permission_denied', {
          context: 'create_page',
          activity_id: selectedActivity?.id,
          challenge_id: challengeId,
          error_message: error.message
        });
      } else if (error.message?.includes('concurrent')) {
        posthog?.capture('concurrent_generation_blocked', {
          context: 'create_page',
          activity_id: selectedActivity?.id,
          template_id: selectedTemplate?.id
        });
        setError(error.message || 'Failed to generate map');
      } else {
        posthog?.capture('map_generation_request_failed', {
          error_message: error.message,
          error_code: error.code,
          activity_id: selectedActivity?.id,
          template_id: selectedTemplate?.id,
          source_page: 'create'
        });
        setError(error.message || 'Failed to generate map');
      }
    } finally {
      reset3DPreparationState();
      setIsGenerating(false);
      setGenerationStatusText('');
    }
  };

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Compute the live legend preview from activity data + customisation. Shared with the
  // referral/event map builders via buildLegendPreview so the two never drift.
  const legendPreview = useMemo(
    () => buildLegendPreview(selectedActivity, {
      customTitle,
      distanceUnit,
      segment: selectedSegment,
    }),
    [selectedActivity, selectedSegment, customTitle, distanceUnit]
  );

  const bestCoarseCandidateId = useMemo(() => {
    let bestCandidate = null;

    map3DDebugCandidates.forEach((candidate) => {
      if (candidate.phase !== 'coarse' || !Number.isFinite(candidate.score)) {
        return;
      }

      if (!bestCandidate || candidate.score > bestCandidate.score) {
        bestCandidate = candidate;
      }
    });

    return bestCandidate?.id || null;
  }, [map3DDebugCandidates]);

  const best2DCandidateId = useMemo(() => {
    let bestCandidate = null;
    map2DDebugCandidates.forEach((candidate) => {
      if (!Number.isFinite(candidate.score)) return;
      if (!bestCandidate || candidate.score > bestCandidate.score) {
        bestCandidate = candidate;
      }
    });
    return bestCandidate?.id || null;
  }, [map2DDebugCandidates]);

  const bestRefineCandidateId = useMemo(() => {
    let bestCandidate = null;

    map3DDebugCandidates.forEach((candidate) => {
      if (candidate.phase !== 'refine' || !Number.isFinite(candidate.score)) {
        return;
      }

      if (!bestCandidate || candidate.score > bestCandidate.score) {
        bestCandidate = candidate;
      }
    });

    return bestCandidate?.id || null;
  }, [map3DDebugCandidates]);

  const selectionDebugGroups = useMemo(() => {
    const groups = new Map();
    const selectionPhases = new Set([
      'selection-start',
      'tighten-zoom',
      'tighten-center',
      'tighten',
      'tighten-revert',
      'selection'
    ]);

    map3DDebugCandidates.forEach((candidate) => {
      if (!selectionPhases.has(candidate.phase)) {
        return;
      }

      if (!Number.isFinite(candidate.selectionAttempt)) {
        return;
      }

      const attempt = candidate.selectionAttempt;
      const existingGroup = groups.get(attempt) || {
        attempt,
        total: Number.isFinite(candidate.selectionTotal) ? candidate.selectionTotal : null,
        cards: [],
        selectionCard: null
      };

      existingGroup.cards.push(candidate);
      if (candidate.phase === 'selection') {
        existingGroup.selectionCard = candidate;
      }
      if (Number.isFinite(candidate.selectionTotal)) {
        existingGroup.total = candidate.selectionTotal;
      }

      groups.set(attempt, existingGroup);
    });

    return Array.from(groups.values())
      .sort((leftGroup, rightGroup) => leftGroup.attempt - rightGroup.attempt)
      .map((group) => ({
        ...group,
        cards: [...group.cards].sort((leftCard, rightCard) => {
          const getSortKey = (candidate) => {
            const stepIndex = Number.isFinite(candidate?.index) ? candidate.index : 0;
            if (candidate.phase === 'selection-start') return 0;
            if (candidate.phase === 'tighten-zoom') return 10 + (stepIndex * 4);
            if (candidate.phase === 'tighten-center') return 11 + (stepIndex * 4);
            if (candidate.phase === 'tighten') return 12 + (stepIndex * 4);
            if (candidate.phase === 'tighten-revert') return 13 + (stepIndex * 4);
            if (candidate.phase === 'selection') return 100;
            return 999;
          };

          const orderDifference = getSortKey(leftCard) - getSortKey(rightCard);
          if (orderDifference !== 0) {
            return orderDifference;
          }
          return (leftCard.index ?? 0) - (rightCard.index ?? 0);
        })
      }));
  }, [map3DDebugCandidates]);

  const getOutsidePaddingAreaRatio = useCallback((candidate) => {
    if (!candidate) {
      return null;
    }

    if (Number.isFinite(candidate.outsidePaddingAreaRatio)) {
      return candidate.outsidePaddingAreaRatio;
    }

    if (
      Number.isFinite(candidate.outsidePaddingBboxArea) &&
      Number.isFinite(candidate.bboxArea) &&
      candidate.bboxArea > 0
    ) {
      return candidate.outsidePaddingBboxArea / candidate.bboxArea;
    }

    return null;
  }, []);

  const formatDebugZoom = useCallback((zoomValue) => {
    if (!Number.isFinite(zoomValue)) {
      return 'n/a';
    }

    return zoomValue.toFixed(2);
  }, []);

  const renderDebugCandidateCard = useCallback((candidate) => {
    const outsidePaddingAreaRatio = getOutsidePaddingAreaRatio(candidate);
    const phaseLabelMap = {
      'coarse': 'coarse (post-center)',
      'selection-start': 'selection start',
      'tighten-zoom': 'tighten zoom',
      'tighten-center': 'tighten center',
      'tighten-revert': 'tighten revert'
    };
    const phaseLabel = phaseLabelMap[candidate.phase] || candidate.phase;

    return (
      <div
        className={`map3d-debug-card ${candidate.id === bestCoarseCandidateId ? 'map3d-debug-card--best' : ''}`}
        key={candidate.id}
      >
        {candidate.id === bestCoarseCandidateId && (
          <div className="map3d-debug-badge">Top coarse</div>
        )}
        {candidate.id === bestRefineCandidateId && (
          <div className="map3d-debug-badge">Selected</div>
        )}
        {candidate.thumbnailDataUrl ? (
          <img
            src={candidate.thumbnailDataUrl}
            alt={`3D candidate ${candidate.phase} ${candidate.index}`}
            className="map3d-debug-image"
          />
        ) : (
          <div className="map3d-debug-image map3d-debug-image--empty">No preview</div>
        )}
        <div className="map3d-debug-meta">
          <div className="map3d-debug-phase">
            {phaseLabel} {candidate.total > 1 && Number.isFinite(candidate.index) ? `${candidate.index}/${candidate.total}` : ''}
          </div>
          {Number.isFinite(candidate.selectionAttempt) && Number.isFinite(candidate.selectionTotal) && (
            <div>Try: {candidate.selectionAttempt}/{candidate.selectionTotal}</div>
          )}
          <div>Score: {Number.isFinite(candidate.score) ? candidate.score.toFixed(0) : 'n/a'}</div>
          <div>Requested: {Number.isFinite(candidate.bearing) ? candidate.bearing.toFixed(1) : 'n/a'} / {Number.isFinite(candidate.pitch) ? candidate.pitch.toFixed(1) : 'n/a'}</div>
          <div>Rendered: {Number.isFinite(candidate.camera?.bearing) ? candidate.camera.bearing.toFixed(1) : 'n/a'} / {Number.isFinite(candidate.camera?.pitch) ? candidate.camera.pitch.toFixed(1) : 'n/a'}</div>
          <div>Forced preview: {candidate.cameraForBoundsRejected ? 'yes' : 'no'}</div>
          <div>Requested zoom: {formatDebugZoom(candidate.requestedZoom)}</div>
          <div>Rendered zoom: {formatDebugZoom(candidate.camera?.zoom)}</div>
          {Number.isFinite(candidate.acceptedTightenSteps) && (
            <div>Accepted tighten steps: {candidate.acceptedTightenSteps}</div>
          )}
          {candidate.decision && (
            <div>
              Decision: {candidate.decision}
              {candidate.decisionReason ? ` (${candidate.decisionReason})` : ''}
            </div>
          )}
          <div>
            Visible: {Number.isFinite(candidate.visiblePixels) ? candidate.visiblePixels : 'n/a'}
            {' / '}
            Area: {Number.isFinite(candidate.footprintRatio) ? candidate.footprintRatio.toFixed(2) : 'n/a'}
          </div>
          <div>
            <strong>
              Visible sample ratio: {Number.isFinite(candidate.visibleSampleRatio) ? candidate.visibleSampleRatio.toFixed(2) : 'n/a'}
            </strong>
          </div>
          <div>
            Visible samples: {Number.isFinite(candidate.visibleSampleCount) ? candidate.visibleSampleCount : 'n/a'}
            {' / '}
            Total samples: {Number.isFinite(candidate.totalSampleCount) ? candidate.totalSampleCount : 'n/a'}
          </div>
          <div>
            Gaps: {Number.isFinite(candidate.internalGapCount) ? candidate.internalGapCount : 'n/a'}
            {' / '}
            Gap ratio: {Number.isFinite(candidate.internalGapRatio) ? candidate.internalGapRatio.toFixed(2) : 'n/a'}
          </div>
          <div>
            Ratios: W {Number.isFinite(candidate.bboxWidthRatio) ? candidate.bboxWidthRatio.toFixed(2) : 'n/a'}
            {' / '}
            H {Number.isFinite(candidate.bboxHeightRatio) ? candidate.bboxHeightRatio.toFixed(2) : 'n/a'}
          </div>
          <div>
            Outside pad area: {Number.isFinite(outsidePaddingAreaRatio) ? outsidePaddingAreaRatio.toFixed(2) : 'n/a'}
          </div>
          <div>Center offset: {Number.isFinite(candidate.centerDistanceNormalized) ? candidate.centerDistanceNormalized.toFixed(3) : 'n/a'}</div>
          <div>
            Coverage reward: {Number.isFinite(candidate.coverageReward) ? candidate.coverageReward.toFixed(0) : 'n/a'}
          </div>
          <div>
            Area reward: {Number.isFinite(candidate.footprintReward) ? candidate.footprintReward.toFixed(0) : 'n/a'}
          </div>
        </div>
      </div>
    );
  }, [
    bestCoarseCandidateId,
    bestRefineCandidateId,
    formatDebugZoom,
    getOutsidePaddingAreaRatio
  ]);

  const bestServer2DCandidateKey = useMemo(() => {
    const best = server2DSearchResult?.bestCandidate;
    return best ? `${best.index}-${best.bearing}` : null;
  }, [server2DSearchResult]);

  const renderServer2DCandidateCard = useCallback((candidate) => {
    const cardKey = `${candidate.index}-${candidate.bearing}`;
    const isBest = bestServer2DCandidateKey === cardKey;
    const m = candidate.metrics || {};
    const cfg = server2DSearchResult?.searchConfig;
    const captureW = cfg?.captureWidth || 1;
    const captureH = cfg?.captureHeight || 1;
    const padding = cfg?.padding;
    const overlays = cfg?.overlays;
    const pct = (n, d) => `${(100 * n / Math.max(1, d)).toFixed(3)}%`;
    return (
      <div
        className={`map3d-debug-card ${isBest ? 'map3d-debug-card--best' : ''}`}
        key={`server-${cardKey}`}
      >
        {isBest && <div className="map3d-debug-badge">Best bearing</div>}
        {candidate.thumbnailDataUrl ? (
          <div style={{ position: 'relative' }}>
            <img
              src={candidate.thumbnailDataUrl}
              alt={`Server 2D candidate ${candidate.index}`}
              className="map3d-debug-image"
            />
            {padding && (
              <div
                style={{
                  position: 'absolute',
                  top: pct(padding.top, captureH),
                  left: pct(padding.left, captureW),
                  width: `calc(${pct(captureW - padding.left - padding.right, captureW)})`,
                  height: `calc(${pct(captureH - padding.top - padding.bottom, captureH)})`,
                  border: '1.5px dashed rgba(255, 255, 255, 0.85)',
                  boxShadow: '0 0 0 1px rgba(17, 24, 39, 0.45)',
                  pointerEvents: 'none',
                }}
              />
            )}
            {overlays?.logo && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: pct(overlays.logo.width, captureW),
                  height: pct(overlays.logo.height, captureH),
                  background: 'rgba(239, 68, 68, 0.18)',
                  border: '1.5px dashed rgba(239, 68, 68, 0.95)',
                  pointerEvents: 'none',
                }}
              />
            )}
            {overlays?.stats && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: pct(overlays.stats.width, captureW),
                  height: pct(overlays.stats.height, captureH),
                  background: 'rgba(59, 130, 246, 0.18)',
                  border: '1.5px dashed rgba(59, 130, 246, 0.95)',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        ) : (
          <div className="map3d-debug-image map3d-debug-image--empty">
            {candidate.thumbnailError ? 'Image failed' : 'No preview'}
          </div>
        )}
        <div className="map3d-debug-meta">
          <div className="map3d-debug-phase">
            server coarse {Number.isFinite(candidate.index) ? `${candidate.index}/${candidate.total}` : ''}
          </div>
          <div>Score: {Number.isFinite(m.score) ? m.score.toFixed(0) : 'n/a'}</div>
          <div>Bearing: {Number.isFinite(candidate.bearing) ? candidate.bearing.toFixed(1) : 'n/a'}°</div>
          <div>Zoom: {Number.isFinite(candidate.camera?.zoom) ? candidate.camera.zoom.toFixed(2) : 'n/a'}</div>
          <div>Center: {Number.isFinite(candidate.camera?.centerLng) ? candidate.camera.centerLng.toFixed(4) : 'n/a'}, {Number.isFinite(candidate.camera?.centerLat) ? candidate.camera.centerLat.toFixed(4) : 'n/a'}</div>
          <div>Footprint: {Number.isFinite(m.footprintRatio) ? m.footprintRatio.toFixed(2) : 'n/a'}</div>
          <div>Ratios: W {Number.isFinite(m.bboxWidthRatio) ? m.bboxWidthRatio.toFixed(2) : 'n/a'} / H {Number.isFinite(m.bboxHeightRatio) ? m.bboxHeightRatio.toFixed(2) : 'n/a'}</div>
          <div>Outside pad area: {Number.isFinite(m.outsidePaddingAreaRatio) ? m.outsidePaddingAreaRatio.toFixed(5) : 'n/a'}</div>
          <div>Center offset: {Number.isFinite(m.centerDistanceNormalized) ? m.centerDistanceNormalized.toFixed(3) : 'n/a'}</div>
          <div>On-screen: {Number.isFinite(m.onScreenRatio) ? m.onScreenRatio.toFixed(2) : 'n/a'} / Inside pad: {Number.isFinite(m.insidePaddingRatio) ? m.insidePaddingRatio.toFixed(2) : 'n/a'}</div>
          <div>Area reward: {Number.isFinite(m.footprintReward) ? m.footprintReward.toFixed(0) : 'n/a'}</div>
          <div>Center penalty: {Number.isFinite(m.centerPenalty) ? m.centerPenalty.toFixed(0) : 'n/a'}</div>
          <div>OOB penalty: {Number.isFinite(m.outOfBoundsPenalty) ? m.outOfBoundsPenalty.toFixed(0) : 'n/a'}</div>
          <div>Logo hits: {Number.isFinite(m.logoHitRatio) ? (m.logoHitRatio * 100).toFixed(1) : 'n/a'}% · Stats hits: {Number.isFinite(m.statsHitRatio) ? (m.statsHitRatio * 100).toFixed(1) : 'n/a'}%</div>
          <div>Overlay penalty: {Number.isFinite(m.overlayPenalty) ? m.overlayPenalty.toFixed(0) : 'n/a'}</div>
        </div>
      </div>
    );
  }, [bestServer2DCandidateKey, server2DSearchResult]);

  const render2DDebugCandidateCard = useCallback((candidate) => {
    return (
      <div
        className={`map3d-debug-card ${candidate.id === best2DCandidateId ? 'map3d-debug-card--best' : ''}`}
        key={candidate.id}
      >
        {candidate.id === best2DCandidateId && (
          <div className="map3d-debug-badge">Best bearing</div>
        )}
        {candidate.thumbnailDataUrl ? (
          <img
            src={candidate.thumbnailDataUrl}
            alt={`2D candidate ${candidate.index}`}
            className="map3d-debug-image"
          />
        ) : (
          <div className="map3d-debug-image map3d-debug-image--empty">No preview</div>
        )}
        <div className="map3d-debug-meta">
          <div className="map3d-debug-phase">
            coarse {Number.isFinite(candidate.index) ? `${candidate.index}/${candidate.total}` : ''}
          </div>
          <div>Score: {Number.isFinite(candidate.score) ? candidate.score.toFixed(0) : 'n/a'}</div>
          <div>Requested bearing: {Number.isFinite(candidate.bearing) ? candidate.bearing.toFixed(1) : 'n/a'}°</div>
          <div>Rendered bearing: {Number.isFinite(candidate.camera?.bearing) ? candidate.camera.bearing.toFixed(1) : 'n/a'}°</div>
          <div>Capture zoom: {formatDebugZoom(candidate.requestedCaptureZoom ?? candidate.captureCamera?.zoom)}</div>
          <div>Display zoom: {formatDebugZoom(candidate.requestedZoom)} / rendered {formatDebugZoom(candidate.camera?.zoom)}</div>
          <div>
            Capture center: {Number.isFinite(candidate.captureCamera?.center?.[0]) ? candidate.captureCamera.center[0].toFixed(4) : 'n/a'}
            {', '}
            {Number.isFinite(candidate.captureCamera?.center?.[1]) ? candidate.captureCamera.center[1].toFixed(4) : 'n/a'}
          </div>
          <div>Footprint: {Number.isFinite(candidate.footprintRatio) ? candidate.footprintRatio.toFixed(2) : 'n/a'}</div>
          <div>Ratios: W {Number.isFinite(candidate.bboxWidthRatio) ? candidate.bboxWidthRatio.toFixed(2) : 'n/a'} / H {Number.isFinite(candidate.bboxHeightRatio) ? candidate.bboxHeightRatio.toFixed(2) : 'n/a'}</div>
          <div>Outside pad area: {Number.isFinite(candidate.outsidePaddingAreaRatio) ? candidate.outsidePaddingAreaRatio.toFixed(5) : 'n/a'}</div>
          <div>Center offset: {Number.isFinite(candidate.centerDistanceNormalized) ? candidate.centerDistanceNormalized.toFixed(3) : 'n/a'}</div>
          <div>On-screen: {Number.isFinite(candidate.onScreenRatio) ? candidate.onScreenRatio.toFixed(2) : 'n/a'} / Inside pad: {Number.isFinite(candidate.insidePaddingRatio) ? candidate.insidePaddingRatio.toFixed(2) : 'n/a'}</div>
          <div>Area reward: {Number.isFinite(candidate.footprintReward) ? candidate.footprintReward.toFixed(0) : 'n/a'}</div>
          <div>Center penalty: {Number.isFinite(candidate.centerPenalty) ? candidate.centerPenalty.toFixed(0) : 'n/a'}</div>
          <div>OOB penalty: {Number.isFinite(candidate.outOfBoundsPenalty) ? candidate.outOfBoundsPenalty.toFixed(0) : 'n/a'}</div>
          <div>Logo hits: {Number.isFinite(candidate.logoHitRatio) ? (candidate.logoHitRatio * 100).toFixed(1) : 'n/a'}% · Stats hits: {Number.isFinite(candidate.statsHitRatio) ? (candidate.statsHitRatio * 100).toFixed(1) : 'n/a'}%</div>
          <div>Overlay penalty: {Number.isFinite(candidate.overlayPenalty) ? candidate.overlayPenalty.toFixed(0) : 'n/a'}</div>
        </div>
      </div>
    );
  }, [best2DCandidateId, formatDebugZoom]);

  const isGenerating3DPreparation = !selectedActivityIsZwift && mapDimension === '3d' && isGenerating && isPreparing3DMap;
  const isGenerating3DFlow = !selectedActivityIsZwift && mapDimension === '3d' && isGenerating;
  const genericGenerationStatusText = t("createPage.generating", "Generating...");
  const showLive3DDebugMap = isAdmin &&
    !selectedActivityIsZwift &&
    mapDimension === '3d' &&
    threeDRenderSource === 'browser' &&
    show3DDebugMaps &&
    (isPreparing3DMap || isShowing3DDebugRenderer);
  const mapPreparationRingStyle = {
    '--progress-angle': `${Math.max(0, Math.min(360, (map3DProgressRatio || 0) * 360))}deg`
  };
  const activityRefreshStatus = isLoadingActivities && allActivities.length > 0
    ? {
      type: 'refreshing',
      message: t('createPage.activitiesRefreshing', 'Refreshing activities')
    }
    : activityRefreshNotice?.message
      ? activityRefreshNotice
      : null;

  return (
    <div className="create-page">
      <div className="create-container">
        <button className="back-button" onClick={() => navigate(-1)}>
          <IconArrowLeft size={20} />
          {t('createPage.back', 'Back')}
        </button>
        <h1 className="create-title">{t('createPage.createYourMyth', 'Create Your Myth')}</h1>

        {/* Free challenge generation banner */}
        {isFreeGeneration && (
          <div className="challenge-gen-banner">
            Free challenge generation &mdash; {challengeStyle} style
          </div>
        )}

        {/* Step 1: Activity Selection */}
        <div className="activity-selection-section">
            <div className="section-header">
              <h2 className="section-title">
                {activityId ? t('createPage.selectedActivity', 'Selected activity') : t('createPage.selectActivity', 'Select activity')}
              </h2>
            </div>
            {activityRefreshStatus && allActivities.length > 0 && (
              <div className={`activities-refresh-status ${activityRefreshStatus.type || 'info'}`}>
                <div className="activities-refresh-content">
                  {activityRefreshStatus.type === 'refreshing' && (
                    <span className="activities-refresh-spinner" aria-hidden="true"></span>
                  )}
                  <span>{activityRefreshStatus.message}</span>
                </div>
                {activityRefreshStatus.action === 'reconnect' && (
                  <button className="activities-refresh-action" onClick={handleStravaReconnect}>
                    {t('createPage.reconnectStrava', 'Reconnect')}
                  </button>
                )}
              </div>
            )}

            {/* Show target activity loading/error states when loading from direct link */}
            {activityId && isLoadingTargetActivity ? (
              <div className="loading-state">{t('createPage.loadingActivity', 'Loading activity...')}</div>
            ) : activityId && targetActivityError ? (
              <div className="error-state">
                <p>{targetActivityError}</p>
                <button className="retry-button" onClick={() => navigate('/create')}>
                  {t('createPage.selectDifferentActivity', 'Select a different activity')}
                </button>
              </div>
            ) : isLoadingActivities && allActivities.length === 0 ? (
              <div className="loading-state">{t('createPage.loadingActivities', 'Loading activities...')}</div>
            ) : stravaError ? (
              <div className="no-activities-message">
                <IconMap size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                {stravaError}
              </div>
            ) : allActivities.length === 0 && !activityId ? (
              <div className="no-activities-message">
                <IconMap size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <p style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>
                  {t('createPage.noActivities', 'No activities found')}
                </p>
                <p style={{ fontSize: '14px', opacity: 0.7 }}>
                  {t('createPage.noActivitiesDesc', 'Please record some activities in Strava first, then come back here to create your mythic maps.')}
                </p>
              </div>
            ) : selectedActivity ? (
              /* Selected activity - shown alone when an activity is selected */
              <div className="selected-activity-container">
                <div
                  className="activity-card selected single"
                  onClick={() => handleActivityClick(selectedActivity)}
                >
                  <div className="activity-map-preview">
                    <ActivityMap
                      key={`map-${selectedActivity.id}-${selectedSegment?.id || 'full'}-${selectedSegment?.segment?.map?.polyline ? 'loaded' : 'pending'}`}
                      activity={selectedActivity}
                      width={220}
                      height={220}
                      selectedSegment={selectedSegment}
                    />
                    {isLoadingSegmentPolyline && (
                      <div className="map-loading-overlay">
                        <div className="map-loading-spinner"></div>
                      </div>
                    )}
                  </div>
                  <div className="activity-info">
                    <p className="activity-date">
                      {new Date(selectedActivity.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="activity-name">{selectedActivity.name}</p>
                    <p className="activity-stats">
                      {(selectedActivity.distance / 1000).toFixed(1)}km • {formatDuration(selectedActivity.moving_time)}
                    </p>
                    <p className="activity-type" style={{ fontSize: '12px', color: '#8a8078', marginTop: '4px' }}>
                      {selectedActivity.type}
                    </p>
                  </div>
                  <div className="selection-check">✓</div>
                </div>
              </div>
            ) : (
              <>
                {/* Activity grid or Strava ID input page */}
                {isOnStravaIdPage ? (
                  <div className="strava-id-input-section">
                    <div className="strava-id-card">
                      <div className="strava-id-icon-wrapper">
                        <IconLink size={28} strokeWidth={1.5} />
                      </div>
                      <p className="strava-id-label">
                        {t('createPage.lookingForOlder', 'Looking for an older activity?')}
                      </p>
                      <p className="strava-id-hint">
                        {t('createPage.stravaIdHint', 'Paste a Strava activity link or ID to load it directly')}
                      </p>
                      <div className="strava-id-input-row">
                        <input
                          type="text"
                          className="strava-id-input"
                          placeholder={t('createPage.stravaIdPlaceholder', 'e.g. strava.com/activities/15366741243')}
                          value={stravaIdInput}
                          onChange={(e) => { setStravaIdInput(e.target.value); setStravaIdError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleStravaIdSubmit(); }}
                        />
                      </div>
                      <button className="strava-id-submit" onClick={handleStravaIdSubmit}>
                        {t('createPage.go', 'Load Activity')} →
                      </button>
                      {stravaIdError && <p className="strava-id-error">{stravaIdError}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="activities-row">
                    {paginatedActivities.map(activity => {
                      const hasGPS = activity.map && activity.map.summary_polyline;
                      return (
                        <div
                          key={activity.id}
                          className={`activity-card ${!hasGPS ? 'disabled no-gps' : ''}`}
                          onClick={() => handleActivityClick(activity)}
                          title={!hasGPS ? t('createPage.noGPSData', 'This activity has no GPS data') : ''}
                        >
                          <div className="activity-map-preview">
                            {hasGPS ? (
                              <ActivityMap activity={activity} width={150} height={150} />
                            ) : (
                              <div style={{
                                width: 150,
                                height: 150,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f0e8',
                                borderRadius: '8px'
                              }}>
                                <IconMap size={32} style={{ opacity: 0.3 }} />
                              </div>
                            )}
                          </div>
                          <div className="activity-info">
                            <p className="activity-date">
                              {new Date(activity.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                            <p className="activity-name">{activity.name}</p>
                            <p className="activity-stats">
                              {(activity.distance / 1000).toFixed(1)}km • {formatDuration(activity.moving_time)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Navigation below activities */}
                {totalPages > 1 && (
                  <div className="activities-navigation">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 0}
                      className="activity-nav-btn"
                    >
                      <IconChevronLeft size={20} />
                    </button>
                    <span className="page-indicator">
                      {currentPage + 1} / {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages - 1}
                      className="activity-nav-btn"
                    >
                      <IconChevronRight size={20} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

        {/* Segments List - Show after activity is selected, only for Ride activities */}
        {selectedActivity && selectedActivity.type === 'Ride' && (
          <div className="segments-section">
            <div
              className={`segments-collapse-header ${isSegmentsExpanded ? 'expanded' : ''}`}
              onClick={() => setIsSegmentsExpanded(!isSegmentsExpanded)}
            >
              <div className="segments-collapse-title">
                <IconRoute size={18} />
                <span>Segments {segmentEfforts.length > 0 ? `(${segmentEfforts.length})` : ''}</span>
                {isLoadingSegments && <div className="segments-loading-indicator"></div>}
              </div>
              <IconChevronRight
                size={18}
                className={`segments-collapse-arrow ${isSegmentsExpanded ? 'rotated' : ''}`}
              />
            </div>
            {isSegmentsExpanded && (
              <SegmentsList
                segments={segmentEfforts}
                selectedSegment={selectedSegment}
                onSegmentSelect={handleSegmentSelect}
                isLoading={isLoadingSegments}
                activityType={selectedActivity.type}
                hideHeader={true}
              />
            )}
          </div>
        )}

        {/* Step 2: Style Selection (hidden for free challenge generations - style is locked) */}
        {selectedActivity && !isFreeGeneration && (
          <StyleSelectionSection
            title={t('createPage.chooseStyle', 'Choose style')}
            isLoading={isLoadingTemplates}
            loadingText={t('createPage.loadingStyles', 'Loading styles...')}
            emptyText={t('createPage.noStylesAvailable', 'No styles available')}
            isEmpty={sortedStyleGroups.length === 0}
            pageItems={paginatedStyles}
            selectedStyleName={selectedStyleGroup?.name || null}
            onSelectStyle={handleStyleSelect}
            onPreloadStyle={preloadStyleGroupExamples}
            getStyleBadge={(styleGroup) => (
              selectedActivityIsZwift && isZwiftMapStyleName(styleGroup.name)
                ? t('createPage.freeStyleBadge', 'Free')
                : null
            )}
            tagFilter={{
              availableTagCategories,
              activeTagCategory,
              activeTag,
              onChangeTagCategory: setActiveTagCategory,
              onChangeTag: setActiveTag,
            }}
            page={currentStylePage}
            totalPages={totalStylePages}
            onPageChange={setCurrentStylePage}
            eventStyles={eventStyleGroups}
          />
        )}

        {/* Step 3: 2D/3D Selection with Style Previews */}
        {selectedActivity && selectedStyleGroup && selectedActivityIsZwift && (
          <p className="zwift-activity-detected-message">
            {t('createPage.zwiftActivityDetected', 'Zwift activity detected')}
          </p>
        )}

        {/* Free Zwift renders never show the chooser: 2D-only worlds are forced
            2D, and Watopia generates both the 2D and 3D maps automatically. */}
        {selectedActivity && selectedStyleGroup && !selectedZwiftActivityForces2D && !isFreeZwiftRender && !force2DMapGeneration && mapGenerationSettings !== null && (
          <div className="dimension-selection-section">
            <h2 className="section-title">{t('createPage.chooseDimension', 'Choose dimension')}</h2>
            
            {/* Hidden 3D map for rendering and capture during generation */}
            {mapDimension === '3d' && threeDRenderSource === 'browser' && selectedStyleGroup.has3D && (isPreparing3DMap || isShowing3DDebugRenderer) && (
              <div
                className={showLive3DDebugMap ? 'map3d-live-debug-section' : 'map3d-hidden-renderer'}
                style={!showLive3DDebugMap ? { position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 } : undefined}
              >
                {showLive3DDebugMap && (
                  <div className="map3d-live-debug-title">Live 3D search map</div>
                )}
                <ActivityMap3D
                  key={`3d-map-${mapFormat}-${selectedSegment?.id || 'full'}`}
                  activity={selectedActivity}
                  selectedSegment={selectedSegment}
                  width={isPortraitMap ? 1024 : 1536}
                  height={isPortraitMap ? 1536 : 1024}
                  displayWidth={showLive3DDebugMap ? (isPortraitMap ? 320 : 480) : (isPortraitMap ? 220 : 280)}
                  displayHeight={showLive3DDebugMap ? (isPortraitMap ? 480 : 320) : (isPortraitMap ? 330 : 210)}
                onCapture={(captureFunc) => {
                  captureMapCallbackRef.current = captureFunc;
                  maybeResolve3DPreparation();
                }}
                  onRenderStart={() => {
                    setMap3DDebugCandidates([]);
                    const nextText = t('createPage.preparing3D', 'Preparing 3D map...');
                    setMap3DProgressText(nextText);
                    setGenerationStatusText(nextText);
                  }}
                  onRenderReady={() => {
                    const readyText = t('createPage.ready3D', '3D map ready');
                    is3DMapReadyRef.current = true;
                    setMap3DProgressText(readyText);
                    maybeResolve3DPreparation();
                  }}
                  onRenderError={(renderError) => {
                    const nextText = renderError?.message || t('createPage.failed3D', 'Failed to prepare 3D map.');
                    setMap3DProgressText(nextText);
                    setGenerationStatusText(nextText);
                    reset3DPreparationState({ clearProgress: false, clearDebugCandidates: false });
                    rejectPending3DPreparation(new Error(nextText));
                  }}
                  onRenderComplete={() => {
                  }}
                  onProgressChange={(nextProgress) => {
                    const nextText = nextProgress || t('createPage.preparing3D', 'Preparing 3D map...');
                    setMap3DProgressText(nextText);
                    setGenerationStatusText(nextText);
                  }}
                  onProgressMetrics={(progressState) => {
                    const nextRatio = typeof progressState?.progressRatio === 'number' ?
                      Math.max(0, Math.min(1, progressState.progressRatio)) :
                      0;
                    setMap3DProgressRatio(nextRatio);
                  }}
                  onDebugCandidate={(candidateDebug) => {
                    allMap3DDebugCandidatesRef.current = [...allMap3DDebugCandidatesRef.current, candidateDebug];
                    setMap3DDebugCandidates((prev) => {
                      const next = [...prev, candidateDebug];
                      return next.slice(-48);
                    });
                  }}
                  onDebugSummary={(summary) => {
                    browser3DCameraSearchSummaryRef.current = summary || null;
                    setBrowser3DCameraSearchSummary(summary || null);
                  }}
                  enableDebugCandidates={isAdmin && mapDimension === '3d' && show3DDebugMaps}
                  enableCameraSearch={isBrowser3DSearchEnabled}
                  // North-up browser captures take the manual-camera branch:
                  // a plain padded fit at bearing 0 without the scored search
                  // (the backend renderer path locks its search to bearing 0
                  // instead). Accepted trade-off for the non-default browser
                  // render mode.
                  autoBearing={!northUpEnabled}
                  bearing={0}
                  debugTransitionDelayMs={0}
                  mapStyleUrl={DEFAULT_3D_MAP_STYLE_URL}
                  pitch={60}
                  lineWidth={effective3DLineWidth ?? undefined}
                  coarseBearingCount={threeDCoarseSearchCount}
                  robustRetryLimit={threeDTightenCandidateCount}
                  coverageRewardMultiplier={threeDCoverageRewardMultiplier}
                  footprintRewardMultiplier={threeDFootprintRewardMultiplier}
                  highDPI={false}
                  showLoadingOverlay={!showLive3DDebugMap}
          />
        </div>
            )}
            
            <DimensionSelectionSection
              styleGroup={selectedStyleGroup}
              dimension={mapDimension}
              onChangeDimension={handleDimensionChange}
              threeDLabel={t('createPage.threeDTerrain', '3D Terrain')}
              twoDLabel={t('createPage.twoDClassic', '2D Classic')}
              show3DOption={selectedStyleGroupHas3DOption}
            />
            {isAdmin && mapDimension === '3d' && !selectedActivityHas3DWorld && (
              <div className="map3d-render-source-card">
                <div className="map3d-render-source-header">
                  <span className="map3d-render-source-title">3D render mode</span>
                  <span className="map3d-render-source-subtitle">
                    Uses the global admin setting by default, but you can override it here for this page.
                  </span>
                </div>
                <div className="map3d-render-source-toggle">
                  <button
                    type="button"
                    className={`map3d-render-source-option ${threeDRenderMode === 'browser_single' ? 'selected' : ''}`}
                    onClick={() => setThreeDRenderMode('browser_single')}
                  >
                    Browser single
                  </button>
                  <button
                    type="button"
                    className={`map3d-render-source-option ${threeDRenderMode === 'browser_search' ? 'selected' : ''}`}
                    onClick={() => setThreeDRenderMode('browser_search')}
                  >
                    Browser search
                  </button>
                  <button
                    type="button"
                    className={`map3d-render-source-option ${threeDRenderMode === 'renderer' ? 'selected' : ''}`}
                    onClick={() => setThreeDRenderMode('renderer')}
                    style={{ gridColumn: '1 / -1' }}
                  >
                    3D renderer backend
                  </button>
                </div>
              </div>
            )}
            {isAdmin && mapDimension === '3d' && selectedActivityHas3DWorld && (
              <div className="map3d-renderer-status-card">
                <div className="map3d-renderer-status-header">
                  <span className="map3d-renderer-status-title">Watopia renderer cache</span>
                  <span className="map3d-render-source-subtitle">
                    {mapFormat === 'portrait' ? 'Portrait' : 'Landscape'} orientation
                  </span>
                </div>
                <div className="map3d-renderer-status-message">
                  Removes the cached RunPod render for the selected route and orientation. The next generation will render it again.
                </div>
                {renderer3DRenderState.message && (
                  <div className="map3d-renderer-status-message">
                    {renderer3DRenderState.message}
                  </div>
                )}
                <div className="map3d-renderer-status-meta">
                  <div>Activity: {selectedActivity?.id || 'n/a'}</div>
                  <div>Segment: {selectedSegment?.segment?.id || 'full route'}</div>
                  <div>Renderer: Watopia</div>
                  <div>Orientation: {mapFormat}</div>
                </div>
                <div className="map3d-renderer-cache-actions">
                  <button
                    type="button"
                    className="map3d-renderer-cache-delete-button"
                    onClick={() => handleRemove3DRendererCache({ orientation: mapFormat, rendererType: 'watopia' })}
                    disabled={Boolean(renderer3DDeletingCacheKey) || !getSelected3DPolylineString()}
                  >
                    <IconTrash size={15} />
                    <span>{renderer3DDeletingCacheKey ? 'Removing cache...' : 'Remove current cache'}</span>
                  </button>
                </div>
                {renderer3DRenderState.error && (
                  <div className="map3d-renderer-status-error">{renderer3DRenderState.error}</div>
                )}
              </div>
            )}
            {isAdmin && mapDimension === '3d' && !selectedActivityHas3DWorld && threeDRenderMode === 'browser_search' && (
              <div className="map3d-score-controls-card">
                <button
                  type="button"
                  className="map3d-score-controls-toggle"
                  onClick={() => setIs3DSearchControlsExpanded((previousValue) => !previousValue)}
                  aria-expanded={is3DSearchControlsExpanded}
                >
                  <div className="map3d-score-controls-toggle-copy">
                    <div className="map3d-score-controls-header">
                      <span className="map3d-render-source-title">3D search + scoring controls</span>
                    </div>
                    <div className="map3d-score-controls-subtitle">
                      Adjust coarse search, tightening, and score weighting when you need to debug the renderer.
                    </div>
                  </div>
                  <IconChevronRight
                    size={18}
                    className={`map3d-score-controls-arrow ${is3DSearchControlsExpanded ? 'expanded' : ''}`}
                  />
                </button>
                {is3DSearchControlsExpanded && (
                  <div className="map3d-score-controls-body">
                    <div className="map3d-score-slider-group">
                      <div className="map3d-score-slider-header">
                        <span className="map3d-score-slider-title">Coarse search candidates</span>
                        <span className="map3d-score-slider-value">{threeDCoarseSearchCount}</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="36"
                        step="1"
                        value={threeDCoarseSearchCount}
                        onChange={(event) => handle3DCoarseSearchCountChange(event.target.value)}
                        className="map3d-score-slider"
                      />
                      <div className="map3d-score-slider-scale">
                        <span>Fewer coarse search angles</span>
                        <span>More coarse search angles</span>
                      </div>
                    </div>
                    <div className="map3d-score-slider-group">
                      <div className="map3d-score-slider-header">
                        <span className="map3d-score-slider-title">Tightening candidates</span>
                        <span className="map3d-score-slider-value">{threeDTightenCandidateCount}</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        step="1"
                        value={threeDTightenCandidateCount}
                        onChange={(event) => handle3DTightenCandidateCountChange(event.target.value)}
                        className="map3d-score-slider"
                      />
                      <div className="map3d-score-slider-scale">
                        <span>Fewer tighten tries</span>
                        <span>More tighten tries</span>
                      </div>
                    </div>
                    <div className="map3d-score-slider-group">
                      <div className="map3d-score-slider-header">
                        <span className="map3d-score-slider-title">Coverage reward multiplier</span>
                        <span className="map3d-score-slider-value">{threeDCoverageRewardMultiplier.toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.05"
                        value={threeDCoverageRewardMultiplier}
                        onChange={(event) => handle3DScoreMultiplierChange(setThreeDCoverageRewardMultiplier, event.target.value)}
                        className="map3d-score-slider"
                      />
                      <div className="map3d-score-slider-scale">
                        <span>Weaker coverage reward</span>
                        <span>Stronger coverage reward</span>
                      </div>
                    </div>
                    <div className="map3d-score-slider-group">
                      <div className="map3d-score-slider-header">
                        <span className="map3d-score-slider-title">Area reward multiplier</span>
                        <span className="map3d-score-slider-value">{threeDFootprintRewardMultiplier.toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.05"
                        value={threeDFootprintRewardMultiplier}
                        onChange={(event) => handle3DScoreMultiplierChange(setThreeDFootprintRewardMultiplier, event.target.value)}
                        className="map3d-score-slider"
                      />
                      <div className="map3d-score-slider-scale">
                        <span>Weaker area reward</span>
                        <span>Stronger area reward</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {isAdmin && mapDimension === '3d' && !selectedActivityHas3DWorld && threeDRenderMode === 'browser_search' && (
              <label className="map3d-debug-toggle">
                <input
                  type="checkbox"
                  checked={show3DDebugMaps}
                  onChange={(event) => {
                    const nextChecked = event.target.checked;
                    setShow3DDebugMaps(nextChecked);
                    if (!nextChecked) {
                      setMap3DDebugCandidates([]);
                      map3DDebugCandidatesRef.current = [];
                      allMap3DDebugCandidatesRef.current = [];
                      resetBrowser3DDebugState();
                    }
                  }}
                />
                <span>Show 3D debug maps</span>
              </label>
            )}
            {isAdmin && mapDimension === '3d' && !selectedActivityHas3DWorld && threeDRenderSource === 'renderer' && (
              <div className="map3d-renderer-status-card">
                <div className="map3d-renderer-status-header">
                  <span className="map3d-renderer-status-title">3D renderer</span>
                  <span className={`map3d-renderer-status-badge map3d-renderer-status-badge--${(renderer3DRenderState.status || 'idle').toLowerCase()}`}>
                    {renderer3DRenderState.status || 'idle'}
                  </span>
                </div>
                <div className="map3d-renderer-status-message">
                  {renderer3DRenderState.message || 'Activity selection pre-renders both orientations in the background. Full Generate reuses the matching cache entry when possible; Render Map Only still uses the direct preview path.'}
                </div>
                <div className="map3d-renderer-cache-grid">
                  {['landscape', 'portrait'].map((orientation) => {
                    const cacheEntry = renderer3DCacheState[orientation];
                    const isCurrentOrientation = mapFormat === orientation;
                    const cacheMatchesCurrentSettings = Boolean(getSelected3DRendererCacheEntry(orientation));
                    const isDeletingCache = Boolean(
                      cacheEntry.cacheKey &&
                      renderer3DDeletingCacheKey === cacheEntry.cacheKey
                    );
                    return (
                      <div
                        key={orientation}
                        className={`map3d-renderer-cache-item ${isCurrentOrientation ? 'map3d-renderer-cache-item--active' : ''}`}
                      >
                        <div className="map3d-renderer-cache-item-header">
                          <span>{orientation === 'portrait' ? 'Portrait cache' : 'Landscape cache'}</span>
                          <span className={`map3d-renderer-status-badge map3d-renderer-status-badge--${(cacheEntry.status || 'idle').toLowerCase()}`}>
                            {cacheEntry.status || 'idle'}
                          </span>
                        </div>
                        <div className="map3d-renderer-cache-item-message">
                          {cacheEntry.message || 'Waiting to start 3D renderer pre-render.'}
                        </div>
                        <div className="map3d-renderer-status-meta">
                          <div>Cache key: {cacheEntry.cacheKey ? `${cacheEntry.cacheKey.slice(0, 8)}…` : 'n/a'}</div>
                          <div>Job: {cacheEntry.jobId || 'n/a'}</div>
                          <div>Worker: {cacheEntry.workerId || 'n/a'}</div>
                          <div>Queue delay: {Number.isFinite(cacheEntry.delayTime) ? `${(cacheEntry.delayTime / 1000).toFixed(1)}s` : 'n/a'}</div>
                          <div>Execution: {Number.isFinite(cacheEntry.executionTime) ? `${(cacheEntry.executionTime / 1000).toFixed(1)}s` : 'n/a'}</div>
                          <div>Reusable for generate: {cacheMatchesCurrentSettings ? 'yes' : 'no'}</div>
                        </div>
                        {cacheEntry.error && (
                          <div className="map3d-renderer-status-error">{cacheEntry.error}</div>
                        )}
                        <div className="map3d-renderer-cache-actions">
                          <button
                            type="button"
                            className="map3d-renderer-cache-delete-button"
                            onClick={() => handleRemove3DRendererCache({ cacheKey: cacheEntry.cacheKey, orientation })}
                            disabled={!cacheEntry.cacheKey || Boolean(renderer3DDeletingCacheKey)}
                          >
                            <IconTrash size={15} />
                            <span>{isDeletingCache ? 'Removing cache...' : 'Remove cache'}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="map3d-renderer-status-meta">
                  <div>Generate orientation cache: {getSelected3DRendererCacheEntry()?.cacheKey ? `${getSelected3DRendererCacheEntry().cacheKey.slice(0, 8)}…` : 'not ready'}</div>
                  <div>Job: {renderer3DRenderState.jobId || 'n/a'}</div>
                  <div>Worker: {renderer3DRenderState.workerId || 'n/a'}</div>
                  <div>Queue delay: {Number.isFinite(renderer3DRenderState.delayTime) ? `${(renderer3DRenderState.delayTime / 1000).toFixed(1)}s` : 'n/a'}</div>
                  <div>Execution: {Number.isFinite(renderer3DRenderState.executionTime) ? `${(renderer3DRenderState.executionTime / 1000).toFixed(1)}s` : 'n/a'}</div>
                </div>
                {renderer3DDebugHtmlUrl && (
                  <div className="map3d-debug-link-row">
                    <a
                      className="map3d-renderer-debug-link"
                      href={renderer3DDebugHtmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconLink size={16} />
                      <span>Open latest renderer debug HTML</span>
                    </a>
                    <a
                      className="map3d-renderer-debug-link"
                      href={renderer3DDebugHtmlUrl}
                      download={debugHtmlDownloadNames.renderer}
                    >
                      <IconDownload size={16} />
                      <span>Download renderer debug HTML</span>
                    </a>
                  </div>
                )}
                {renderer3DRenderState.error && (
                  <div className="map3d-renderer-status-error">{renderer3DRenderState.error}</div>
                )}
              </div>
            )}
            {isAdmin && mapDimension === '3d' && !selectedActivityHas3DWorld && threeDRenderMode === 'browser_search' && show3DDebugMaps && map3DDebugCandidates.length > 0 && (
              <div className="map3d-debug-section">
                <div className="map3d-debug-header">
                  <span>3D candidate debug</span>
                  <span>{map3DDebugCandidates.length} candidates</span>
                </div>
                {browser3DDebugHtmlUrl && (
                  <div className="map3d-debug-link-row">
                    <a
                      className="map3d-renderer-debug-link"
                      href={browser3DDebugHtmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconLink size={16} />
                      <span>Open latest browser debug HTML</span>
                    </a>
                    <a
                      className="map3d-renderer-debug-link"
                      href={browser3DDebugHtmlUrl}
                      download={debugHtmlDownloadNames.browser}
                    >
                      <IconDownload size={16} />
                      <span>Download browser debug HTML</span>
                    </a>
                  </div>
                )}
                <div className="map3d-debug-grid">
                  {map3DDebugCandidates.map(renderDebugCandidateCard)}
                </div>
                {selectionDebugGroups.length > 0 && (
                  <div className="map3d-debug-groups">
                    {selectionDebugGroups.map((group) => (
                      <div className="map3d-debug-group" key={`selection-group-${group.attempt}`}>
                        <div className="map3d-debug-group-header">
                          <span className="map3d-debug-group-title">
                            Try {group.attempt}{Number.isFinite(group.total) ? `/${group.total}` : ''}
                          </span>
                          {group.selectionCard?.decision && (
                            <span className="map3d-debug-group-status">
                              {group.selectionCard.decision}
                            </span>
                          )}
                        </div>
                        <div className="map3d-debug-group-cards">
                          {group.cards.map(renderDebugCandidateCard)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isAdmin && mapDimension === '2d' && selectedActivity && (
              <div className="map3d-debug-section">
                <div className="map3d-debug-header">
                  <span>2D bearing search (debug)</span>
                  <label className="map3d-debug-toggle" style={{ marginTop: 0 }}>
                    <input
                      type="checkbox"
                      checked={show2DBearingSearch}
                      onChange={(event) => {
                        const nextChecked = event.target.checked;
                        setShow2DBearingSearch(nextChecked);
                        if (nextChecked) {
                          setMap2DDebugCandidates([]);
                          setBrowser2DCameraSearchSummary(null);
                          setBrowser2DDebugHtml('');
                          setMap2DSearchRunId((prev) => prev + 1);
                        }
                      }}
                    />
                    <span>Run 2D bearing search</span>
                  </label>
                </div>
                {show2DBearingSearch && (
                  <>
                    <div className="map3d-live-debug-section">
                      <div className="map3d-live-debug-title">Live 2D bearing search</div>
                      <ActivityMap2DSearch
                        key={`2d-search-${selectedActivity?.id}-${selectedSegment?.id || 'full'}-${isPortraitMap ? 'portrait' : 'landscape'}-${map2DSearchRunId}`}
                        activity={selectedActivity}
                        selectedSegment={selectedSegment}
                        width={isPortraitMap ? 854 : 1280}
                        height={isPortraitMap ? 1280 : 853}
                        displayWidth={isPortraitMap ? 320 : 480}
                        displayHeight={isPortraitMap ? 480 : 320}
                        padding={map2DSearchPadding}
                        lineWidth={map2DSearchRouteStyle.lineWidth}
                        lineColor={map2DSearchRouteStyle.lineColor}
                        lineOpacity={map2DSearchRouteStyle.lineOpacity}
                        logoOverlayWidth={map2DSearchOverlays.logoWidth}
                        logoOverlayHeight={map2DSearchOverlays.logoHeight}
                        statsOverlayWidth={map2DSearchOverlays.statsWidth}
                        statsOverlayHeight={map2DSearchOverlays.statsHeight}
                        coarseBearingCount={12}
                        stepDelayMs={250}
                        enableDebugCandidates
                        onDebugCandidate={(candidate) => {
                          setMap2DDebugCandidates((prev) => [...prev, candidate]);
                        }}
                        onDebugSummary={(summary) => {
                          setBrowser2DCameraSearchSummary(summary || null);
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
                      {map2DDebugCandidates.length} candidates evaluated
                      {browser2DCameraSearchSummary?.bestCandidate && (
                        <>
                          {' · '}best bearing {browser2DCameraSearchSummary.bestCandidate.bearing.toFixed(0)}°
                          {' (score '}{browser2DCameraSearchSummary.bestCandidate.score.toFixed(0)}{')'}
                        </>
                      )}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-tertiary)' }}>
                      Capture padding ({isPortraitMap ? 'portrait' : 'landscape'}): TB {map2DSearchPadding.top}px / LR {map2DSearchPadding.left}px
                      {mapGenerationSettings === null ? ' · loading settings…' : ''}
                    </div>
                    {browser2DDebugHtmlUrl && (
                      <div className="map3d-debug-link-row">
                        <a
                          className="map3d-renderer-debug-link"
                          href={browser2DDebugHtmlUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconLink size={16} />
                          <span>Open latest 2D debug HTML</span>
                        </a>
                        <a
                          className="map3d-renderer-debug-link"
                          href={browser2DDebugHtmlUrl}
                          download={debugHtmlDownloadNames.browser2d}
                        >
                          <IconDownload size={16} />
                          <span>Download 2D debug HTML</span>
                        </a>
                      </div>
                    )}
                    <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: 16 }}>
                      <div>
                        <div className="map3d-live-debug-title" style={{ marginBottom: 6 }}>
                          Browser search · {map2DDebugCandidates.length} candidates
                          {browser2DCameraSearchSummary?.bestCandidate && (
                            <> · best {browser2DCameraSearchSummary.bestCandidate.bearing.toFixed(0)}° (score {browser2DCameraSearchSummary.bestCandidate.score.toFixed(0)})</>
                          )}
                        </div>
                        {map2DDebugCandidates.length > 0 ? (
                          <div className="map3d-debug-grid">
                            {map2DDebugCandidates.map(render2DDebugCandidateCard)}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Waiting for browser search…</div>
                        )}
                      </div>
                      <div>
                        <div className="map3d-live-debug-title" style={{ marginBottom: 6 }}>
                          Server (Firebase) search · {server2DSearchStatus}
                          {server2DSearchResult?.bestCandidate && (
                            <> · best {server2DSearchResult.bestCandidate.bearing.toFixed(0)}° (score {server2DSearchResult.bestCandidate.metrics?.score?.toFixed(0)})</>
                          )}
                          {Number.isFinite(server2DSearchResult?.timings?.searchDurationMs) && (
                            <> · search {server2DSearchResult.timings.searchDurationMs}ms</>
                          )}
                          {Number.isFinite(server2DSearchResult?.timings?.debugImagesDurationMs) && (
                            <> · images {server2DSearchResult.timings.debugImagesDurationMs}ms</>
                          )}
                        </div>
                        {server2DSearchStatus === 'running' && (
                          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Running server search…</div>
                        )}
                        {server2DSearchStatus === 'error' && (
                          <div style={{ fontSize: 12, color: '#b91c1c' }}>Error: {server2DSearchError}</div>
                        )}
                        {server2DSearchStatus === 'done' && Array.isArray(server2DSearchResult?.candidates) && server2DSearchResult.candidates.length > 0 && (
                          <div className="map3d-debug-grid">
                            {server2DSearchResult.candidates.map(renderServer2DCandidateCard)}
                          </div>
                        )}
                        {server2DSearchStatus === 'done' && (!server2DSearchResult?.candidates || server2DSearchResult.candidates.length === 0) && (
                          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Server returned no candidates.</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Map Format Toggle */}
        {selectedActivity && selectedStyleGroup && (
          <FormatSelectionSection
            title={t('createPage.chooseFormat', 'Choose format')}
            format={mapFormat}
            onChangeFormat={(fmt) => {
              reset3DPreparationState({ rejectPending: true });
              setGenerationStatusText('');
              setMapFormat(fmt);
              setMap3DDebugCandidates([]);
            }}
            portraitLabel={t('createPage.portraitMap', 'Portrait Map (4:5)')}
            landscapeLabel={t('createPage.landscapeMap', 'Landscape Map')}
          />
        )}

        {/* Legend Customisation - collapsible, default collapsed */}
        {selectedActivity && selectedStyleGroup && (
          <LegendSection
            title={t('createPage.customiseLegend', 'Customise legend')}
            mapTitleLabel={t('createPage.mapTitle', 'Map title')}
            unitsLabel={t('createPage.units', 'Units')}
            placeholder={selectedActivity.name}
            customTitle={customTitle}
            onChangeTitle={setCustomTitle}
            distanceUnit={distanceUnit}
            onChangeUnit={setDistanceUnit}
            isExpanded={isLegendExpanded}
            onToggleExpanded={setIsLegendExpanded}
            legendPreview={legendPreview}
          />
        )}

        {/* Generate Button */}
        {selectedActivity && selectedTemplate && (
          <div className="generate-section">
            <div className="generate-actions">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || isRunning3DDebugPreview || isRendering3DOnly || (!isFreeGeneration && !isFreeZwiftRender && effectiveRemaining <= 0)}
                className="generate-btn"
              >
                {isGenerating ? (
                  isGenerating3DPreparation ? (
                    <>
                      <div className="generate-progress-ring" style={mapPreparationRingStyle} aria-hidden="true">
                        <div className="generate-progress-ring-inner"></div>
                      </div>
                      {t("createPage.preparingMap", "Preparing Map")}
                    </>
                  ) : (
                    <>
                      <div className="spinner-small"></div>
                      {generationStatusText || genericGenerationStatusText}
                    </>
                  )
                ) : isFreeZwiftRender ? (
                  t("createPage.generateFreeZwiftMap", "Generate Free Zwift Map")
                ) : (
                  t("createPage.generateMap", "Generate Map")
                )}
              </button>
              {isAdmin && mapDimension === '3d' && !selectedActivityHas3DWorld && (
                <button
                  onClick={handleRenderMapOnly}
                  disabled={isGenerating || isRunning3DDebugPreview || isRendering3DOnly}
                  className="generate-btn generate-btn-secondary"
                >
                  {isRendering3DOnly ? (
                    <>
                      <div className="spinner-small"></div>
                      Render Map Only
                    </>
                  ) : (
                    'Render Map Only'
                  )}
                </button>
              )}
              {isAdmin && mapDimension === '3d' && !selectedActivityHas3DWorld && threeDRenderMode === 'browser_search' && show3DDebugMaps && (
                <button
                  onClick={handleShow3DDebugMaps}
                  disabled={isGenerating || isRunning3DDebugPreview || isRendering3DOnly}
                  className="generate-btn generate-btn-secondary"
                >
                  {isRunning3DDebugPreview ? (
                    <>
                      <div className="spinner-small"></div>
                      {t('createPage.showDebugMaps', 'Show Debug Maps')}
                    </>
                  ) : (
                    t('createPage.showDebugMaps', 'Show Debug Maps')
                  )}
                </button>
              )}
            </div>
            {isGenerating && generationStatusText && generationStatusText !== genericGenerationStatusText && !isGenerating3DFlow && (
              <div className="generate-status-text">{generationStatusText}</div>
            )}
            {isRunning3DDebugPreview && map3DProgressText && (
              <div className="generate-status-text">{map3DProgressText}</div>
            )}
            {isRendering3DOnly && generationStatusText && (
              <div className="generate-status-text">{generationStatusText}</div>
            )}
            {captured3DImage?.objectUrl && (
              <div className="render-only-preview-card">
                <div className="render-only-preview-header">
                  <span className="render-only-preview-title">Rendered Map Preview</span>
                  <a
                    href={captured3DImage.objectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="render-only-preview-link"
                  >
                    <IconLink size={16} />
                    <span>Open image</span>
                  </a>
                </div>
                <img
                  src={captured3DImage.objectUrl}
                  alt="Rendered 3D map preview"
                  className="render-only-preview-image"
                />
                <div className="render-only-preview-meta">
                  <div>Source: {captured3DImage.source}</div>
                  <div>Type: {captured3DImage.mimeType || 'image/png'}</div>
                  <div>Size: {Number.isFinite(captured3DImage.size) ? `${(captured3DImage.size / 1024).toFixed(1)} KB` : 'n/a'}</div>
                </div>
              </div>
            )}
            {/* Show error message if no diamonds left OR if there's another error */}
            {((!isFreeGeneration && !isFreeZwiftRender && effectiveRemaining <= 0) || error) && (
              <div className="error-message">
                {!isFreeGeneration && !isFreeZwiftRender && (effectiveRemaining <= 0 || error === 'noDiamonds') ? (
                  <>
                    <p>{t('createPage.noDiamondsLeft', 'You have no diamonds left.')}</p>
                    <p className="error-message-link">
                      <a href="/diamonds" onClick={(e) => { e.preventDefault(); navigate('/diamonds'); }}>
                        {t('createPage.getMoreDiamonds', 'Get more diamonds for free')}
                      </a>
                    </p>
                  </>
                ) : error === 'alreadyClaimed' ? (
                  <p>{t('createPage.alreadyClaimed', 'You have already created a free map for this activity.')}</p>
                ) : (
                  <p>{error}</p>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      <Footer />
    </div>
  );
};

export default CreatePage;
